import React, { useEffect, useRef, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import Table from '@editorjs/table';
import axios from 'axios';

const DEFAULT_API_URL = window.location.protocol === 'https:'
  ? 'https://api-blog.wpmp.synology.me'
  : `http://${window.location.hostname}:8000`;
const API_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;
const POSTS_CACHE_KEY = 'nbap_posts_summary_v1';
const IMAGE_GUIDE_PATTERN = /^\[이미지 가이드:\s*(.+?)\]$/i;

const getValidationVerdict = (postOrReport) =>
  postOrReport?.overall_verdict ||
  postOrReport?.verdict ||
  postOrReport?.validation_verdict ||
  'unknown';
const getTrustScorePercent = (postOrReport) =>
  Math.round(Number(
    postOrReport?.trust_score ??
    postOrReport?.validation_trust_score ??
    0
  ) * 100);
const stripHtml = (text = '') => text.replace(/<[^>]*>?/gm, '');
const tableRowsToText = (rows = []) => rows
  .map(row => row.map(cell => stripHtml(cell || '')).join(' | '))
  .join('\n');
const tableRowsToHtml = (rows = [], withHeadings = false) => {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const [headRow, ...bodyRows] = rows;
  const thead = withHeadings
    ? `<thead><tr>${headRow.map(cell => `<th style="border:1px solid #d0d7de;padding:8px;background:#f6f8fa;text-align:left;">${cell || ''}</th>`).join('')}</tr></thead>`
    : '';
  const renderedBodyRows = (withHeadings ? bodyRows : rows)
    .map(row => `<tr>${row.map(cell => `<td style="border:1px solid #d0d7de;padding:8px;vertical-align:top;">${cell || ''}</td>`).join('')}</tr>`)
    .join('');
  return `<table style="width:100%;border-collapse:collapse;margin:18px 0 20px;">${thead}<tbody>${renderedBodyRows}</tbody></table>`;
};
const getValidationColor = (report) => {
  const verdict = getValidationVerdict(report);
  if (verdict === 'pass') return '#27ae60';
  if (verdict === 'caution') return '#f39c12';
  return '#e74c3c';
};
const getValidationBackground = (report) => {
  const verdict = getValidationVerdict(report);
  if (verdict === 'pass') return '#e8f8f5';
  if (verdict === 'caution') return '#fdf2e9';
  return '#fae5e3';
};
const getValidationState = (post) => {
  if (!post) return 'none';
  if (post.status === 'processing') return 'writing';
  if (post.validation_status === 'validating') return 'validating';
  if (post.correction_status === 'correcting') return 'correcting';
  if (
    post.validation_status === 'validated' ||
    post.validation_report ||
    post.validation_verdict ||
    post.validation_trust_score !== undefined
  ) return 'validated';
  if (post.validation_status === 'failed') return 'failed';
  if (post.status === 'completed') return 'pending';
  return 'none';
};
const getValidationBadge = (post) => {
  const state = getValidationState(post);
  if (state === 'writing') return { text: '작성 중', color: '#4a90e2', background: '#eaf2fd', border: '#4a90e2' };
  if (state === 'correcting') return { text: '교정 중...', color: '#8e44ad', background: '#f4ecf7', border: '#8e44ad' };
  if (state === 'validating') return { text: '검증 중...', color: '#8e44ad', background: '#f4ecf7', border: '#8e44ad' };
  if (state === 'pending') return { text: '검증 전', color: '#7f8c8d', background: '#f4f6f7', border: '#bdc3c7' };
  if (state === 'failed') return { text: '검증 실패', color: '#e74c3c', background: '#fae5e3', border: '#e74c3c' };
  if (state === 'validated') {
    return {
      text: `검증 신뢰도: ${getTrustScorePercent(post.validation_report || post)}% (${getValidationVerdict(post.validation_report || post).toUpperCase()})`,
      color: getValidationColor(post.validation_report || post),
      background: getValidationBackground(post.validation_report || post),
      border: getValidationColor(post.validation_report || post)
    };
  }
  return null;
};
const getCorrectionSuggestions = (report) => report?.correction_suggestions || [];
const getAppliedSuggestions = (report) => report?.applied_suggestions || [];
const getRemainingRisks = (report) => report?.remaining_risks || [];
const formatEventTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const getStageLabel = (stage) => {
  if (stage === 'write') return '작성';
  if (stage === 'validation') return '검증';
  if (stage === 'correction') return '교정';
  return stage || '작업';
};
const getEventColor = (status) => {
  if (status === 'failed') return '#e74c3c';
  if (status === 'running' || status === 'started') return '#8e44ad';
  return '#27ae60';
};
const getCorrectionButtonLabel = (post, localCorrectingId) => {
  if (!post) return '교정 적용';
  if (post.validation_status === 'validating') return '검증 중...';
  if (post.correction_status === 'correcting' || localCorrectingId === post.id) return '교정 중...';
  if (
    !post.validation_report &&
    post.validation_status !== 'validated' &&
    !post.validation_verdict &&
    post.validation_trust_score === undefined
  ) return '검증 후 교정';
  return '교정 적용';
};
const isCorrectionButtonDisabled = (post, localCorrectingId) => (
  !post ||
  post.status === 'processing' ||
  post.validation_status === 'validating' ||
  post.correction_status === 'correcting' ||
  localCorrectingId === post.id
);

const getGenerationModeBadge = (post) => {
  if (!post || post.generation_mode !== 'test') return null;
  return {
    text: 'Beta',
    color: '#0f766e',
    background: '#ccfbf1',
    border: '#14b8a6'
  };
};

const getRenderableTags = (post) => {
  if (!post || !Array.isArray(post.tags)) return [];
  return post.tags
    .filter(tag => typeof tag === 'string' && tag.trim() !== '')
    .map(tag => {
      const trimmed = tag.trim();
      return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    });
};
const getRenderableTagLine = (post) => getRenderableTags(post).join(' ');
const appendTagBlockToContent = (content, post) => {
  const tagLine = getRenderableTagLine(post);
  const clonedContent = content ? JSON.parse(JSON.stringify(content)) : { blocks: [] };
  const blocks = Array.isArray(clonedContent.blocks) ? clonedContent.blocks : [];
  if (!tagLine) {
    clonedContent.blocks = blocks;
    return clonedContent;
  }

  clonedContent.blocks = blocks
    .filter(block => !(block.type === 'paragraph' && stripHtml(block?.data?.text || '').trim() === tagLine))
    .concat({
      type: 'paragraph',
      data: {
        text: tagLine
      }
    });

  return clonedContent;
};

const extractImageGuideText = (text = '') => {
  const normalized = stripHtml(text).replace(/\u00a0/g, ' ').trim();
  const match = normalized.match(IMAGE_GUIDE_PATTERN);
  return match ? match[1].trim() : '';
};

const getImageGuideBlocks = (content) => {
  const blocks = Array.isArray(content?.blocks) ? content.blocks : [];
  return blocks.reduce((acc, block, blockIndex) => {
    if (block?.type !== 'paragraph') return acc;
    const guideText = extractImageGuideText(block?.data?.text || '');
    if (!guideText) return acc;
    acc.push({
      key: block?.id || `guide-${blockIndex}-${guideText}`,
      blockIndex,
      guideText,
      rawText: stripHtml(block?.data?.text || '').trim()
    });
    return acc;
  }, []);
};

const normalizeImageGuideStatus = (status, imageUrl) => {
  const normalized = `${status || ''}`.trim().toLowerCase();
  if (imageUrl || ['complete', 'completed', 'done', 'success', 'succeeded', 'generated', 'ready'].includes(normalized)) {
    return 'completed';
  }
  if (['queued', 'pending', 'processing', 'running', 'started', 'submitted', 'generating', 'in_progress'].includes(normalized)) {
    return 'processing';
  }
  if (['failed', 'error', 'cancelled'].includes(normalized)) {
    return 'failed';
  }
  return 'idle';
};

const normalizeImageGuideUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return `${API_URL}/${url.replace(/^\.\//, '')}`;
};

const normalizeAsyncImageStatus = (status, imageUrl) => {
  const normalized = `${status || ''}`.trim().toLowerCase();
  if (imageUrl || ['complete', 'completed', 'done', 'success', 'succeeded', 'generated', 'ready'].includes(normalized)) {
    return 'completed';
  }
  if (['queued', 'pending', 'processing', 'running', 'started', 'submitted', 'generating', 'in_progress'].includes(normalized)) {
    return 'processing';
  }
  if (['failed', 'error', 'cancelled', 'canceled'].includes(normalized)) {
    return 'failed';
  }
  return 'idle';
};

const findImageUrlsInContent = (content) => {
  const blocks = Array.isArray(content?.blocks) ? content.blocks : [];
  return blocks.reduce((acc, block) => {
    const imageUrl = block?.type === 'image' ? normalizeImageGuideUrl(block?.data?.file?.url) : '';
    if (imageUrl) acc.push(imageUrl);
    return acc;
  }, []);
};

const resolveViewerImageFromDetail = (detail, currentUrl, fallbackUrl) => {
  const imageUrls = findImageUrlsInContent(detail?.content);
  if (!imageUrls.length) return fallbackUrl || currentUrl || '';
  if (currentUrl && imageUrls.includes(currentUrl)) return currentUrl;
  if (fallbackUrl && imageUrls.includes(fallbackUrl)) return fallbackUrl;
  return imageUrls[0];
};

const createViewerState = (imageUrl = '') => ({
  isOpen: Boolean(imageUrl),
  imageUrl,
  originalImageUrl: imageUrl,
  editPrompt: '',
  referenceImages: [],
  selectedReferenceImageId: '',
  isHovered: false,
  isEditing: false,
  isSubmittingRequest: false,
  editStatus: 'idle',
  editedImageUrl: '',
  editJobId: '',
  error: ''
});

const buildClientImageEditRequestId = (postId, imageUrl = '') => {
  const sanitizedUrl = `${imageUrl}`.split('/').pop()?.replace(/[^a-zA-Z0-9_-]/g, '') || 'image';
  return `imgedit-${postId}-${sanitizedUrl}-${Date.now()}`;
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(`${reader.result || ''}`);
    reader.onerror = () => reject(reader.error || new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });

const normalizeReferenceImageFilename = (name = '', index = 0) => {
  const trimmed = `${name || ''}`.trim();
  if (trimmed) return trimmed;
  return `reference-${index + 1}.png`;
};

const normalizeImageEditJobMap = (value) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [key, job]) => {
    const normalizedKey = normalizeImageGuideUrl(key);
    if (!normalizedKey || !job || typeof job !== 'object') return acc;
    acc[normalizedKey] = job;
    return acc;
  }, {});
};

const findEditorImageElement = (target) => {
  if (!(target instanceof Element)) return null;
  if (target.tagName === 'IMG' && target.getAttribute('src')) {
    return target;
  }
  const nearestImage = target.closest('img[src]');
  if (nearestImage) return nearestImage;
  const editorImageWrapper = target.closest('.image-tool, .cdx-block.image, .ce-block');
  if (!editorImageWrapper) return null;
  return editorImageWrapper.querySelector('img[src]');
};

const isProbablyMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent || '';
  const touchCapable = window.navigator.maxTouchPoints > 0;
  return /Android|iPhone|iPad|iPod/i.test(userAgent) || touchCapable;
};

const replaceImageGuideBlocksWithImages = (content, guideJobMap) => {
  const clonedContent = content ? JSON.parse(JSON.stringify(content)) : { blocks: [] };
  const blocks = Array.isArray(clonedContent.blocks) ? clonedContent.blocks : [];
  clonedContent.blocks = blocks.map((block, blockIndex) => {
    if (block?.type !== 'paragraph') return block;
    const guideText = extractImageGuideText(block?.data?.text || '');
    if (!guideText) return block;
    const key = block?.id || `guide-${blockIndex}-${guideText}`;
    const job = guideJobMap?.[key];
    if (!job || job.status !== 'completed' || !job.imageUrl) return block;
    return {
      type: 'image',
      data: {
        file: { url: job.imageUrl },
        caption: '',
        withBorder: false,
        withBackground: false,
        stretched: false
      }
    };
  });
  return clonedContent;
};

const SERIES_POSITION_ORDER = {
  before_1: 1,
  before_2: 2,
  before_3: 3,
  current: 4,
  after_1: 5,
  after_2: 6,
  after_3: 7,
};

const SERIES_ROLE_ORDER = {
  hub: 0,
  'problem-discovery': 1,
  comparison: 2,
  recommendation: 4,
  'issue-explainer': 4,
  'practical-check': 5,
  conversion: 6,
  other: 7,
  current: 4,
};

const stripGenerationTitle = (title = '') =>
  title
    .replace(/^\[테스트\]\[생성중\]\s*/g, '')
    .replace(/^\[생성중\]\s*/g, '');

const getSeriesGroupTitle = (post) => post?.series_group_title || stripGenerationTitle(post?.title || '');

const getSeriesGroupCompletionStatus = (post) => post?.series_group_status || 'draft';

const compareSeriesPosts = (a, b) => {
  const sortOrderDiff = (a.series_sort_order ?? 999) - (b.series_sort_order ?? 999);
  if (sortOrderDiff !== 0) return sortOrderDiff;
  const positionDiff = (SERIES_POSITION_ORDER[a.series_position] || 999) - (SERIES_POSITION_ORDER[b.series_position] || 999);
  if (positionDiff !== 0) return positionDiff;
  const roleDiff = (SERIES_ROLE_ORDER[a.series_role] || 999) - (SERIES_ROLE_ORDER[b.series_role] || 999);
  if (roleDiff !== 0) return roleDiff;
  const depthDiff = (a.series_depth || 0) - (b.series_depth || 0);
  if (depthDiff !== 0) return depthDiff;
  return b.id - a.id;
};

const makeSeriesSuggestionItemKey = (item = {}) => (
  [
    item.position || '',
    item.role || '',
    stripGenerationTitle(item.title || '').trim(),
    item.topic || '',
  ].join('::')
);

const findGeneratedSeriesPost = (posts, selectedPost, item) => {
  if (!selectedPost || !item || item.is_current_article) return null;
  if (item.generated_post_id) {
    const matchedById = (posts || []).find(post => post.id === item.generated_post_id);
    if (matchedById) return matchedById;
  }
  const normalizedTitle = stripGenerationTitle(item.title || '').trim();
  const suggestionItemKey = makeSeriesSuggestionItemKey(item);
  return (posts || []).find(post => {
    if (post.series_parent_post_id !== selectedPost.id) return false;
    if (post.source_suggestion_item_key && post.source_suggestion_item_key === suggestionItemKey) {
      return true;
    }
    if ((post.series_sort_order ?? null) !== (item.sort_order ?? null)) {
      const samePosition = (post.series_position || '') === (item.position || '');
      const sameRole = (post.series_role || '') === (item.role || '');
      const sameTitle = stripGenerationTitle(post.title || '').trim() === normalizedTitle;
      if (!(samePosition && sameRole && sameTitle)) return false;
    }
    return true;
  }) || null;
};

const getRootPostForTab = (postsById, post) => {
  if (!post) return null;
  const rootId = post.series_root_post_id || post.id;
  return postsById.get(rootId) || post;
};

const isSeriesGroupMember = (post) => Boolean(post?.series_group_title || post?.series_root_post_id || post?.series_parent_post_id);
const isSeriesRelayConnectionError = (message = '') =>
  typeof message === 'string' && message.includes('All connection attempts failed');

const hasMeaningfulSeriesSuggestion = (payload) =>
  Array.isArray(payload?.suggested_articles) && payload.suggested_articles.length > 0;

const hasPersistedSeriesSuggestion = (post) =>
  Array.isArray(post?.series_suggestion_payload?.suggested_articles) &&
  post.series_suggestion_payload.suggested_articles.length > 0;

const isPendingSeriesSuggestionPayload = (payload) => {
  const suggestedArticles = payload?.suggested_articles;
  if (Array.isArray(suggestedArticles) && suggestedArticles.length > 0) {
    return false;
  }
  const roleLabel = `${payload?.current_article_role?.label || ''}`.trim().toLowerCase();
  const verdict = `${payload?.series_verdict || ''}`.trim().toLowerCase();
  return roleLabel === 'pending' || verdict === 'pending';
};

const isPostVisibleInViewMode = (postsById, post, viewMode) => {
  const rootPost = getRootPostForTab(postsById, post);
  const effectiveStatus = isSeriesGroupMember(post)
    ? (rootPost?.series_group_status || 'draft')
    : (rootPost?.status || post.status);
  if (viewMode === 'published') return effectiveStatus === 'published';
  return effectiveStatus !== 'published';
};

function App() {
  const editorRef = useRef(null);
  const seriesPollTimeoutRef = useRef(null);
  const seriesPollAttemptsRef = useRef(0);
  const imageGuidePollTimeoutRef = useRef(null);
  const imageGuidePollAttemptsRef = useRef(0);
  const viewerEditPollTimeoutRef = useRef(null);
  const viewerEditPollAttemptsRef = useRef(0);
  const viewerReferenceInputRef = useRef(null);
  const skipDirtyResetRef = useRef(false);
  const selectedPostIdRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [generationModel, setGenerationModel] = useState('gpt');
  const [isImageGenerationEnabled, setIsImageGenerationEnabled] = useState(true);
  const [generationVersion, setGenerationVersion] = useState('stable');
  const [isVersionInfoOpen, setIsVersionInfoOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);
  const [seriesStatus, setSeriesStatus] = useState({ status: 'idle', failure_history: [] });
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState('');
  const [seriesSuggestion, setSeriesSuggestion] = useState(null);
  const [seriesRetrying, setSeriesRetrying] = useState(false);
  const [seriesGeneratingAll, setSeriesGeneratingAll] = useState(false);
  const [seriesGeneratingKeys, setSeriesGeneratingKeys] = useState([]);
  const [isSeriesAnalysisOpen, setIsSeriesAnalysisOpen] = useState(true);
  const [expandedSeriesGroups, setExpandedSeriesGroups] = useState({});
  const [correctingPostId, setCorrectingPostId] = useState(null);
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityError, setActivityError] = useState('');
  const [isCorrectionHistoryOpen, setIsCorrectionHistoryOpen] = useState(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false); 
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'published'
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination & Display limit states
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Image Viewer state
  const [viewer, setViewer] = useState(createViewerState());
  const [imageGuideJobs, setImageGuideJobs] = useState({});
  const [imageGuideError, setImageGuideError] = useState('');
  const [isImageGuideStarting, setIsImageGuideStarting] = useState(false);
  const [isImageGuideStatusLoading, setIsImageGuideStatusLoading] = useState(false);
  const [imageEditJobs, setImageEditJobs] = useState({});
  const [imageEditNotice, setImageEditNotice] = useState('');
  const viewerPromptRef = useRef(null);
  const imageEditJobsRef = useRef({});
  const editorDecorationFrameRef = useRef(null);

  const hasProcessingPosts = posts.some(p =>
    p.status === 'processing' ||
    p.validation_status === 'validating' ||
    p.correction_status === 'correcting'
  );

  const clearSeriesPolling = () => {
    if (seriesPollTimeoutRef.current) {
      clearTimeout(seriesPollTimeoutRef.current);
      seriesPollTimeoutRef.current = null;
    }
    seriesPollAttemptsRef.current = 0;
  };

  const clearImageGuidePolling = () => {
    if (imageGuidePollTimeoutRef.current) {
      clearTimeout(imageGuidePollTimeoutRef.current);
      imageGuidePollTimeoutRef.current = null;
    }
    imageGuidePollAttemptsRef.current = 0;
  };

  const clearViewerEditPolling = () => {
    if (viewerEditPollTimeoutRef.current) {
      clearTimeout(viewerEditPollTimeoutRef.current);
      viewerEditPollTimeoutRef.current = null;
    }
    viewerEditPollAttemptsRef.current = 0;
  };

  useEffect(() => {
    imageEditJobsRef.current = normalizeImageEditJobMap(imageEditJobs);
  }, [imageEditJobs]);

  const cancelEditorDecorationFrame = () => {
    if (editorDecorationFrameRef.current) {
      cancelAnimationFrame(editorDecorationFrameRef.current);
      editorDecorationFrameRef.current = null;
    }
  };

  const createImageEditOverlayElement = () => {
    const overlay = document.createElement('div');
    overlay.dataset.nbapImageEditOverlay = 'true';
    overlay.style.position = 'absolute';
    overlay.style.inset = '0';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.background = 'rgba(15, 23, 42, 0.38)';
    overlay.style.pointerEvents = 'none';
    overlay.style.borderRadius = '8px';
    overlay.style.zIndex = '3';
    overlay.innerHTML = '<div style="padding:10px 14px;border-radius:999px;background:rgba(15,23,42,0.88);color:#fff;font-size:13px;font-weight:700;box-shadow:0 8px 24px rgba(0,0,0,0.25);">편집 진행중...</div>';
    return overlay;
  };

  const applyEditorImageEditDecorations = () => {
    const editorContainer = document.getElementById('editorjs');
    if (!editorContainer) return;

    editorContainer.querySelectorAll('img[src]').forEach((img) => {
      const normalizedSrc = normalizeImageGuideUrl(img.getAttribute('src'));
      const job = imageEditJobsRef.current[normalizedSrc];
      const wrapper = img.closest('.image-tool, .ce-block, .cdx-block') || img.parentElement;
      if (!wrapper) return;

      wrapper.style.position = 'relative';
      img.style.opacity = job?.status === 'processing' ? '0.45' : '1';
      const overlay = wrapper.querySelector('[data-nbap-image-edit-overlay="true"]');

      if (job?.status === 'processing') {
        if (!overlay) {
          wrapper.appendChild(createImageEditOverlayElement());
        }
        return;
      }

      if (overlay) {
        overlay.remove();
      }
    });

    editorContainer.querySelectorAll('[data-nbap-image-edit-overlay="true"]').forEach((overlay) => {
      const wrapper = overlay.parentElement;
      const img = wrapper?.querySelector('img[src]');
      if (!img) {
        overlay.remove();
        return;
      }
      const normalizedSrc = normalizeImageGuideUrl(img.getAttribute('src'));
      const job = imageEditJobsRef.current[normalizedSrc];
      if (job?.status !== 'processing') {
        overlay.remove();
      }
    });
  };

  const scheduleEditorImageEditDecorations = () => {
    cancelEditorDecorationFrame();
    editorDecorationFrameRef.current = requestAnimationFrame(() => {
      editorDecorationFrameRef.current = null;
      applyEditorImageEditDecorations();
    });
  };

  const isOverlayOnlyMutation = (mutation) => {
    const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
    if (!changedNodes.length) return false;
    return changedNodes.every((node) => node instanceof HTMLElement && node.dataset?.nbapImageEditOverlay === 'true');
  };

  const attachEditorImageInteractions = () => {
    const editorContainer = document.getElementById('editorjs');
    if (!editorContainer) return () => {};

    const markImagesClickable = () => {
      editorContainer.querySelectorAll('img[src]').forEach((img) => {
        img.style.cursor = 'zoom-in';
      });
      scheduleEditorImageEditDecorations();
    };

    const handleEditorClick = (e) => {
      const imageEl = findEditorImageElement(e.target);
      if (!imageEl) return;
      const src = imageEl.getAttribute('src');
      if (!src) return;
      e.preventDefault();
      e.stopPropagation();
      setViewer(createViewerState(src));
    };

    markImagesClickable();
    editorContainer.addEventListener('click', handleEditorClick, true);

    const observer = new MutationObserver((mutations) => {
      if (mutations.length && mutations.every(isOverlayOnlyMutation)) {
        return;
      }
      markImagesClickable();
    });
    observer.observe(editorContainer, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      editorContainer.removeEventListener('click', handleEditorClick, true);
      cancelEditorDecorationFrame();
    };
  };

  const isCurrentSeriesTarget = (postId) => selectedPostIdRef.current === postId;

  useEffect(() => {
    if (!viewer.isOpen) return;
    setViewer(prev => prev.isHovered ? prev : ({ ...prev, isHovered: true }));
    const timer = setTimeout(() => {
      if (viewerPromptRef.current && typeof viewerPromptRef.current.focus === 'function') {
        viewerPromptRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [viewer.isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scheduleEditorImageEditDecorations();
    }, 0);
    return () => clearTimeout(timer);
  }, [selectedPost?.id, selectedPost?.content, imageEditJobs]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(POSTS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setPosts(parsed);
        }
      }
    } catch (err) {
      console.error('Failed to load cached posts', err);
    }
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/posts`);
      setPosts(res.data);
      try {
        localStorage.setItem(POSTS_CACHE_KEY, JSON.stringify(res.data));
      } catch (err) {
        console.error('Failed to cache posts', err);
      }
      if (selectedPost) {
        const updated = res.data.find(p => p.id === selectedPost.id);
        if (updated) {
          const currentValidationVerdict =
            selectedPost.validation_report?.overall_verdict ||
            selectedPost.validation_report?.verdict ||
            selectedPost.validation_verdict ||
            null;
          const currentValidationTrust =
            selectedPost.validation_report?.trust_score ??
            selectedPost.validation_trust_score ??
            null;
          const statusChanged = updated.status !== selectedPost.status ||
            updated.validation_status !== selectedPost.validation_status ||
            updated.correction_status !== selectedPost.correction_status ||
            updated.validation_verdict !== currentValidationVerdict ||
            updated.validation_trust_score !== currentValidationTrust ||
            updated.title !== selectedPost.title ||
            updated.series_root_post_id !== selectedPost.series_root_post_id ||
            updated.series_parent_post_id !== selectedPost.series_parent_post_id ||
            updated.series_depth !== selectedPost.series_depth ||
            updated.series_position !== selectedPost.series_position ||
            updated.series_role !== selectedPost.series_role ||
            updated.series_group_title !== selectedPost.series_group_title ||
            updated.series_sort_order !== selectedPost.series_sort_order ||
            updated.series_group_status !== selectedPost.series_group_status ||
            updated.series_suggestion_status !== selectedPost.series_suggestion_status ||
            updated.series_suggestion_generated_at !== selectedPost.series_suggestion_generated_at ||
            updated.series_suggestion_artifact_dir !== selectedPost.series_suggestion_artifact_dir ||
            updated.source_suggestion_item_key !== selectedPost.source_suggestion_item_key;
          if (!isDirty && statusChanged) {
             setSelectedPost(prev => prev ? {
               ...prev,
               ...updated,
               content: prev.content,
               tags: prev.tags,
               validation_report: prev.validation_report,
               correction_report: prev.correction_report
             } : updated);
             setTitle(prevTitle => (prevTitle === updated.title ? prevTitle : updated.title));
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch posts", err);
    }
  };

  const fetchPostDetail = async (postId) => {
    const res = await axios.get(`${API_URL}/posts/${postId}`);
    return res.data;
  };

  const refreshPostAfterImageMutation = async (postId) => {
    if (!postId) return null;
    const detail = await fetchPostDetail(postId);
    applySelectedPostDetail(detail);
    if (selectedPostIdRef.current === detail.id && detail.content) {
      skipDirtyResetRef.current = true;
      initEditor(detail.content, detail);
      setIsDirty(false);
    }
    await fetchPosts();
    return detail;
  };

  useEffect(() => {
    let interval;
    if (hasProcessingPosts) interval = setInterval(fetchPosts, 3000);
    return () => clearInterval(interval);
  }, [hasProcessingPosts]);

  useEffect(() => { fetchPosts(); }, []);

  const fetchActivityEvents = async (postId) => {
    try {
      const res = await axios.get(`${API_URL}/posts/${postId}/activity`);
      setActivityEvents(res.data.events || []);
      setActivityError(res.data.error || '');
    } catch (err) {
      setActivityEvents([]);
      setActivityError('진행 로그를 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    if (!selectedPost) {
      setActivityEvents([]);
      setActivityError('');
      return undefined;
    }

    fetchActivityEvents(selectedPost.id);
    const active =
      selectedPost.status === 'processing' ||
      selectedPost.validation_status === 'validating' ||
      selectedPost.correction_status === 'correcting';

    if (!active) return undefined;
    const interval = setInterval(() => fetchActivityEvents(selectedPost.id), 3000);
    return () => clearInterval(interval);
  }, [selectedPost?.id, selectedPost?.status, selectedPost?.validation_status, selectedPost?.correction_status]);

  const initEditor = (data, post) => {
    if (editorRef.current && typeof editorRef.current.destroy === 'function') {
      if (typeof editorRef.current.__detachImageInteractions === 'function') {
        editorRef.current.__detachImageInteractions();
      }
      editorRef.current.destroy();
    }
    if (!document.getElementById('editorjs')) return;

    const clonedData = appendTagBlockToContent(data, post);
    if (clonedData.blocks) {
      clonedData.blocks = clonedData.blocks.map(block => {
        if (block.type === 'image' && block.data.file.url.startsWith('/uploads/')) {
          block.data.file.url = `${API_URL}${block.data.file.url}`;
        }
        if (block.type === 'image' && block.data) {
          block.data.caption = '';
        }
        return block;
      });
    }

    let detachImageInteractions = () => {};
    editorRef.current = new EditorJS({
      holder: 'editorjs',
      tools: { 
        header: Header, 
        list: List,
        table: {
          class: Table,
          inlineToolbar: true,
          config: {
            rows: 2,
            cols: 3,
            withHeadings: true
          }
        },
        image: {
          class: ImageTool,
          config: { 
            uploader: {
              uploadByFile: async (file) => {
                const formData = new FormData();
                formData.append('image', file);
                const res = await axios.post(`${API_URL}/upload-image`, formData);
                if (res.data.success) {
                  return { success: 1, file: { url: `${API_URL}${res.data.file.url}` } };
                }
              }
            }
          }
        }
      },
      data: clonedData,
      placeholder: '내용을 입력하세요...',
      onChange: () => setIsDirty(true),
      onReady: () => {
        detachImageInteractions();
        detachImageInteractions = attachEditorImageInteractions();
      }
    });
    editorRef.current.__detachImageInteractions = detachImageInteractions;
  };

  useEffect(() => {
    if (selectedPost?.content) {
      const timer = setTimeout(() => {
        initEditor(selectedPost.content, selectedPost);
        if (skipDirtyResetRef.current) {
          skipDirtyResetRef.current = false;
        } else {
          setIsDirty(false);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedPost?.id, selectedPost?.content]);

  useEffect(() => {
    selectedPostIdRef.current = selectedPost?.id || null;
    setIsCorrectionHistoryOpen(false);
    setIsActivityOpen(false);
    setIsSeriesModalOpen(false);
    setIsSeriesAnalysisOpen(false);
    setSeriesSuggestion(null);
    setSeriesStatus({ status: 'idle', failure_history: [] });
    setSeriesError('');
    setImageGuideJobs({});
    setImageGuideError('');
    setIsImageGuideStarting(false);
    setIsImageGuideStatusLoading(false);
    setImageEditJobs({});
    setImageEditNotice('');
    setViewer(createViewerState());
    clearSeriesPolling();
    clearImageGuidePolling();
    clearViewerEditPolling();
  }, [selectedPost?.id]);

  useEffect(() => () => clearSeriesPolling(), []);
  useEffect(() => () => clearImageGuidePolling(), []);
  useEffect(() => () => clearViewerEditPolling(), []);

  useEffect(() => {
    if (selectedPost?.id) {
      fetchSeriesStatus(selectedPost.id);
    }
  }, [selectedPost?.id]);

  useEffect(() => {
    if (!selectedPost?.id) return;
    const guides = getImageGuideBlocks(selectedPost.content);
    if (!guides.length) {
      clearImageGuidePolling();
      setIsImageGuideStarting(false);
      setIsImageGuideStatusLoading(false);
      setImageGuideError('');
      setImageGuideJobs({});
      return;
    }

    setImageGuideError('');
    setIsImageGuideStatusLoading(true);
    imageGuidePollAttemptsRef.current = 0;
    fetchImageGuideStatus(selectedPost.id, guides);
  }, [selectedPost?.id, selectedPost?.content]);

  useEffect(() => {
    if (!selectedPost?.id) return undefined;
    const imageUrls = findImageUrlsInContent(selectedPost.content);
    if (!imageUrls.length) {
      setImageEditJobs({});
      return undefined;
    }

    let cancelled = false;
    fetchViewerEditStatus(selectedPost.id)
      .then((res) => {
        if (cancelled) return;
        const payload = extractViewerEditStatusPayload(res.data);
        const targetImageUrl = payload.targetImageUrl || payload.imageUrl;
        if (payload.status === 'processing' && targetImageUrl) {
          setImageEditJobs({
            [targetImageUrl]: {
              status: 'processing',
              jobId: payload.jobId,
              requestId: payload.requestId,
              targetImageUrl,
              imageUrl: targetImageUrl,
              error: ''
            }
          });
          clearViewerEditPolling();
          viewerEditPollAttemptsRef.current = 0;
          viewerEditPollTimeoutRef.current = setTimeout(() => {
            pollViewerEditStatus(selectedPost.id, payload.jobId, targetImageUrl, payload.requestId);
          }, 1200);
          return;
        }
        if (payload.status === 'completed' && payload.imageUrl) {
          const completedImageUrl = payload.imageUrl;
          const hasCompletedImage = imageUrls.includes(completedImageUrl);
          const stillHasTargetImage = payload.targetImageUrl && imageUrls.includes(payload.targetImageUrl);
          if (!hasCompletedImage || stillHasTargetImage) {
            refreshPostAfterImageMutation(selectedPost.id).catch((err) => {
              console.error('편집 완료 후 글 상세 동기화 실패:', err);
            });
          }
        }
        setImageEditJobs({});
      })
      .catch(() => {
        if (!cancelled) setImageEditJobs({});
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPost?.id, selectedPost?.content]);

  const selectPost = (post) => {
    if (isDirty && !window.confirm('저장되지 않은 변경사항이 있습니다. 무시하고 이동하시겠습니까?')) {
      return;
    }
    if (!post) {
      setSelectedPost(null);
      setTitle('');
      setIsDirty(false);
      return;
    }
    setSelectedPost(post);
    setTitle(post.title);
    setIsDirty(false);
    fetchPostDetail(post.id)
      .then((detail) => {
        setSelectedPost(detail);
        setTitle(detail.title);
      })
      .catch(() => {
        alert('글 상세 정보를 불러오지 못했습니다.');
      });
  };

  const handleGenerate = async () => {
    if (!topic) return alert('주제를 입력하세요.');
    setIsGenerating(true);
    try {
      const endpoint = generationVersion === 'beta' ? '/generate-test' : '/generate';
      await axios.post(`${API_URL}${endpoint}`, {
        topic,
        llm_provider: generationModel,
        image_generation_enabled: isImageGenerationEnabled,
      });
      await fetchPosts();
      setTopic('');
      alert(generationVersion === 'beta' ? 'AI 베타 에이전트 가동 시작!' : 'AI 에이전트 가동 시작!');
    } catch (err) { alert('생성 실패'); } finally { setIsGenerating(false); }
  };

  const handleDelete = async (e, postId) => {
    e.stopPropagation();
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API_URL}/posts/${postId}`);
      if (selectedPost?.id === postId) { setSelectedPost(null); setTitle(''); }
      fetchPosts();
    } catch (err) { alert('삭제 실패'); }
  };

  const handleSeriesDelete = async (e, postId) => {
    e.stopPropagation();
    if (!window.confirm('이 시리즈 그룹과 하위 글을 모두 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete(`${API_URL}/posts/${postId}/series-delete`);
      if (selectedPost?.id && res.data?.affected_ids?.includes(selectedPost.id)) {
        setSelectedPost(null);
        setTitle('');
      }
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || '시리즈 삭제 실패');
    }
  };

  const handleSave = async () => {
    if (!selectedPost || !editorRef.current) return;
    try {
      const savedContent = await editorRef.current.save();
      const processedBlocks = savedContent.blocks.map(block => {
        if (block.type === 'image' && block.data.file.url.includes('/uploads/')) {
          const parts = block.data.file.url.split('/uploads/');
          block.data.file.url = `/uploads/${parts[parts.length - 1]}`;
        }
        return block;
      });
      await axios.patch(`${API_URL}/posts/${selectedPost.id}`, { 
        title, 
        content: { ...savedContent, blocks: processedBlocks } 
      });
      setIsDirty(false);
      alert('저장되었습니다!');
      fetchPosts();
    } catch (err) { alert('저장 실패'); }
  };

  const handleQuickStatusChange = async (e, post) => {
    e.stopPropagation();
    const newStatus = post.status === 'published' ? 'completed' : 'published';
    try {
      await axios.patch(`${API_URL}/posts/${post.id}`, { status: newStatus });
      if (selectedPost?.id === post.id) {
          setSelectedPost(null);
          setTitle('');
      }
      fetchPosts();
    } catch (err) { alert('상태 변경 실패'); }
  };

  const handleSeriesStatusChange = async (e, post) => {
    e.stopPropagation();
    try {
      const res = await axios.post(`${API_URL}/posts/${post.id}/series-complete`);
      await fetchPosts();
      if (selectedPost?.id && res.data?.affected_ids?.includes(selectedPost.id)) {
        const detail = await fetchPostDetail(selectedPost.id);
        setSelectedPost(detail);
        setTitle(detail.title);
      }
    } catch (err) {
      alert(err.response?.data?.detail || '시리즈 완료 처리 실패');
    }
  };

  const handleMarkAsPublished = async () => {
    if (!selectedPost) return;
    const newStatus = selectedPost.status === 'published' ? 'completed' : 'published';
    try {
      await axios.patch(`${API_URL}/posts/${selectedPost.id}`, { status: newStatus });
      alert(newStatus === 'published' ? '발행 완료로 표시되었습니다.' : '미발행 상태로 되돌렸습니다.');
      setSelectedPost(null);
      fetchPosts();
    } catch (err) { alert('상태 변경 실패'); }
  };

  const handleCorrectPost = async () => {
    if (!selectedPost) return;
    const message = selectedPost.validation_report
      ? '검증 리포트 기준으로 본문을 자동 교정하고 다시 검증합니다. 진행할까요?'
      : '먼저 현재 본문을 검증한 뒤, 필요한 경우 자동 교정과 재검증을 이어서 실행합니다. 진행할까요?';
    if (!window.confirm(message)) return;
    setCorrectingPostId(selectedPost.id);
    try {
      await axios.post(`${API_URL}/posts/${selectedPost.id}/correct`);
      await fetchActivityEvents(selectedPost.id);
      await fetchPosts();
    } catch (err) {
      alert(err.response?.data?.detail || '교정 요청 실패');
    } finally {
      setCorrectingPostId(null);
    }
  };

  const syncCompletedGuideImages = async (postId, jobMap) => {
    const hasCompletedGuide = Object.values(jobMap || {}).some(job => job?.status === 'completed' && job?.imageUrl);
    if (!hasCompletedGuide) return;
    try {
      const latestDetail = await fetchPostDetail(postId);
      if (!isCurrentSeriesTarget(postId)) return;
      const nextContent = replaceImageGuideBlocksWithImages(latestDetail?.content, jobMap);
      const nextDetail = JSON.stringify(nextContent) === JSON.stringify(latestDetail?.content)
        ? latestDetail
        : { ...latestDetail, content: nextContent };
      setSelectedPost(prev => (prev && prev.id === postId ? nextDetail : prev));
      setTitle(prevTitle => (prevTitle === nextDetail.title ? prevTitle : nextDetail.title));
      return;
    } catch (err) {
      console.error('Failed to refresh post detail after image generation', err);
    }

    const currentContent = editorRef.current
      ? await editorRef.current.save()
      : selectedPost?.content;
    const nextContent = replaceImageGuideBlocksWithImages(currentContent, jobMap);
    if (JSON.stringify(nextContent) === JSON.stringify(currentContent)) return;
    setSelectedPost(prev => (prev && prev.id === postId ? { ...prev, content: nextContent } : prev));
  };

  const mergeImageGuideJobs = (guides, nextEntries = []) => {
    const fallbackKeys = Array.isArray(guides) ? guides.map(guide => guide.key) : [];
    const merged = {};

    fallbackKeys.forEach((key) => {
      const guide = guides.find(item => item.key === key);
      merged[key] = {
        key,
        guideText: guide?.guideText || '',
        blockIndex: guide?.blockIndex ?? null,
        status: imageGuideJobs[key]?.status || 'idle',
        imageUrl: imageGuideJobs[key]?.imageUrl || '',
        error: imageGuideJobs[key]?.error || ''
      };
    });

    nextEntries.forEach((entry, index) => {
      const rawKey = entry?.key || entry?.guide_key || entry?.id || entry?.block_id;
      const resolvedGuide = guides.find(guide =>
        guide.key === rawKey ||
        guide.blockIndex === entry?.block_index ||
        guide.guideText === entry?.guide_text ||
        guide.guideText === entry?.prompt
      ) || guides[index];
      const key = resolvedGuide?.key || rawKey;
      if (!key) return;
      merged[key] = {
        ...(merged[key] || {}),
        key,
        guideText: resolvedGuide?.guideText || entry?.guide_text || entry?.prompt || merged[key]?.guideText || '',
        blockIndex: resolvedGuide?.blockIndex ?? entry?.block_index ?? merged[key]?.blockIndex ?? null,
        status: normalizeImageGuideStatus(entry?.status, entry?.image_url || entry?.imageUrl || entry?.url),
        imageUrl: normalizeImageGuideUrl(entry?.image_url || entry?.imageUrl || entry?.url),
        error: entry?.error || ''
      };
    });

    return merged;
  };

  const fetchImageGuideStatus = async (postId, guides) => {
    if (!postId || !guides.length) return;
    try {
      const res = await axios.get(`${API_URL}/posts/${postId}/image-guides/status`);
      const jobs = res.data?.guides || res.data?.items || res.data?.jobs || [];
      const merged = mergeImageGuideJobs(guides, jobs);
      setImageGuideJobs(merged);
      await syncCompletedGuideImages(postId, merged);

      const hasPending = Object.values(merged).some(job => job.status === 'processing');
      if (hasPending) {
        setIsImageGuideStatusLoading(false);
        if (imageGuidePollAttemptsRef.current >= 60) {
          clearImageGuidePolling();
          setImageGuideError('이미지 생성이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
          return;
        }
        imageGuidePollAttemptsRef.current += 1;
        imageGuidePollTimeoutRef.current = setTimeout(() => {
          fetchImageGuideStatus(postId, guides);
        }, 2000);
        return;
      }

      clearImageGuidePolling();
      setIsImageGuideStarting(false);
      setIsImageGuideStatusLoading(false);
    } catch (err) {
      clearImageGuidePolling();
      setIsImageGuideStarting(false);
      setIsImageGuideStatusLoading(false);
      setImageGuideError(err.response?.data?.detail || '이미지 생성 상태를 불러오지 못했습니다.');
    }
  };

  const handleGenerateImageGuides = async (guides) => {
    if (!selectedPost?.id || !guides.length) return;
    clearImageGuidePolling();
    setImageGuideError('');
    setIsImageGuideStarting(true);
    setIsImageGuideStatusLoading(false);
    setImageGuideJobs(prev => {
      const next = { ...prev };
      guides.forEach((guide) => {
        next[guide.key] = {
          key: guide.key,
          guideText: guide.guideText,
          blockIndex: guide.blockIndex,
          status: 'processing',
          imageUrl: '',
          error: ''
        };
      });
      return next;
    });

    try {
      const res = await axios.post(`${API_URL}/posts/${selectedPost.id}/image-guides/generate`, {
        guides: guides.map(guide => ({
          key: guide.key,
          block_index: guide.blockIndex,
          guide_text: guide.guideText
        }))
      });
      const jobs = res.data?.guides || res.data?.items || res.data?.jobs || [];
      const merged = mergeImageGuideJobs(guides, jobs);
      setImageGuideJobs(merged);
      await syncCompletedGuideImages(selectedPost.id, merged);
      imageGuidePollAttemptsRef.current = 0;
      imageGuidePollTimeoutRef.current = setTimeout(() => {
        fetchImageGuideStatus(selectedPost.id, guides);
      }, 1200);
    } catch (err) {
      clearImageGuidePolling();
      setIsImageGuideStarting(false);
      setIsImageGuideStatusLoading(false);
      setImageGuideJobs(prev => {
        const next = { ...prev };
        guides.forEach((guide) => {
          next[guide.key] = {
            ...(next[guide.key] || {}),
            key: guide.key,
            guideText: guide.guideText,
            blockIndex: guide.blockIndex,
            status: 'failed',
            error: err.response?.data?.detail || '이미지 생성 요청 실패'
          };
        });
        return next;
      });
      setImageGuideError(err.response?.data?.detail || '이미지 생성 요청에 실패했습니다.');
    }
  };

  const fetchSeriesStatus = async (postId) => {
    if (!postId) return null;
    try {
      const res = await axios.get(`${API_URL}/posts/${postId}/series-suggestions/status`);
      const payload = res.data || { status: 'idle', failure_history: [] };
      if (isCurrentSeriesTarget(postId)) {
        setSeriesStatus(payload);
      }
      return payload;
    } catch (err) {
      const detail = err.response?.data?.detail || '시리즈 제안 상태를 불러오지 못했습니다.';
      const fallback = isSeriesRelayConnectionError(detail)
        ? { status: 'unavailable', last_error: '시리즈 제안 서비스에 연결할 수 없습니다.', failure_history: [] }
        : { status: 'failed', last_error: detail, failure_history: [] };
      if (isCurrentSeriesTarget(postId)) {
        setSeriesStatus(fallback);
      }
      return fallback;
    }
  };

  const startSeriesSuggestionGeneration = async (postId) => {
    if (!postId) return;
    setSeriesRetrying(true);
    setSeriesLoading(true);
    setSeriesError('');
    setSeriesStatus(prev => ({ ...prev, status: 'generating' }));
    try {
      const res = await axios.post(`${API_URL}/posts/${postId}/series-suggestions/retry`);
      if (isCurrentSeriesTarget(postId)) {
        setSeriesStatus(res.data || { status: 'generating', failure_history: [] });
      }
    } catch (err) {
      if (isCurrentSeriesTarget(postId)) {
        setSeriesLoading(false);
        setSeriesError(err.response?.data?.detail || '시리즈 제안 재시도에 실패했습니다.');
      }
    } finally {
      setSeriesRetrying(false);
    }
  };

  const fetchSeriesSuggestion = async (postId, { silent = false } = {}) => {
    if (!postId) return;
    if (!silent) {
      setSeriesLoading(true);
      setSeriesError('');
    }
    try {
      const res = await axios.get(`${API_URL}/posts/${postId}/series-suggestions`);
      const payload = res.data;
      const hasSuggestions = Array.isArray(payload?.suggested_articles) && payload.suggested_articles.length > 0;
      const isPendingPayload = isPendingSeriesSuggestionPayload(payload);
      const isPending = payload?.status === 'pending';
      const isFailed = payload?.status === 'failed';
      const isIdle = payload?.status === 'idle';
      const isGenerated = payload?.status === 'generated';
      const resolvedStatus = hasSuggestions
        ? (isGenerated ? 'generated' : 'ready')
        : (isPending || isPendingPayload)
          ? 'generating'
          : isFailed
            ? 'failed'
            : isIdle
              ? 'idle'
              : seriesStatus?.status;

      if (!isCurrentSeriesTarget(postId)) {
        return;
      }

      setSeriesSuggestion(payload || null);
      setSeriesStatus(prev => ({
        ...prev,
        status: resolvedStatus,
        failure_history: payload?.failure_history || prev.failure_history || []
      }));

      if (hasSuggestions) {
        clearSeriesPolling();
        setSeriesError('');
        setSeriesLoading(false);
        return;
      }

      if (isPending || isPendingPayload) {
        if (seriesPollAttemptsRef.current >= 20) {
          clearSeriesPolling();
          setSeriesLoading(false);
          setSeriesError('시리즈 제안 생성이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
          return;
        }
        seriesPollAttemptsRef.current += 1;
        setSeriesError('');
        setSeriesLoading(true);
        seriesPollTimeoutRef.current = setTimeout(() => {
          fetchSeriesSuggestion(postId, { silent: true });
        }, 2000);
        return;
      }

      if (isFailed) {
        clearSeriesPolling();
        setSeriesLoading(false);
        setSeriesError(payload?.error || '시리즈 제안 생성에 실패했습니다.');
        return;
      }

      if (isIdle) {
        if (seriesStatus?.status === 'generating' || seriesRetrying) {
          if (seriesPollAttemptsRef.current >= 20) {
            clearSeriesPolling();
            setSeriesLoading(false);
            setSeriesError('시리즈 제안 생성이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
            return;
          }
          seriesPollAttemptsRef.current += 1;
          setSeriesLoading(true);
          seriesPollTimeoutRef.current = setTimeout(() => {
            fetchSeriesSuggestion(postId, { silent: true });
          }, 1200);
          return;
        }
        clearSeriesPolling();
        setSeriesLoading(false);
        setSeriesError('');
        return;
      }

      clearSeriesPolling();
      setSeriesLoading(false);
      setSeriesError(payload?.error || '시리즈 제안 결과가 비어 있습니다.');
    } catch (err) {
      if (!isCurrentSeriesTarget(postId)) {
        return;
      }
      clearSeriesPolling();
      setSeriesLoading(false);
      setSeriesError(err.response?.data?.detail || '시리즈 제안을 불러오지 못했습니다.');
      setSeriesSuggestion(null);
    }
  };

  const handleOpenSeriesModal = async () => {
    if (!selectedPost) return;
    clearSeriesPolling();
    seriesPollAttemptsRef.current = 0;
    setIsSeriesModalOpen(true);
    setIsSeriesAnalysisOpen(true);
    setSeriesSuggestion(null);
    setSeriesError('');
    const status = await fetchSeriesStatus(selectedPost.id);
    if (status?.status === 'ready' || status?.status === 'generated' || status?.status === 'generating') {
      await fetchSeriesSuggestion(selectedPost.id);
      return;
    }
    if (status?.status === 'idle') {
      await startSeriesSuggestionGeneration(selectedPost.id);
      seriesPollTimeoutRef.current = setTimeout(() => {
        fetchSeriesSuggestion(selectedPost.id, { silent: true });
      }, 1200);
      return;
    }
    if (status?.status === 'unavailable') {
      setSeriesLoading(false);
      setSeriesError(status?.last_error || '시리즈 제안 서비스에 연결할 수 없습니다.');
      return;
    }
    setSeriesLoading(false);
    setSeriesError(status?.last_error || '이전 시리즈 제안 생성이 실패했습니다.');
  };

  const handleRetrySeriesSuggestion = async () => {
    if (!selectedPost) return;
    clearSeriesPolling();
    seriesPollAttemptsRef.current = 0;
    setSeriesSuggestion(null);
    setSeriesError('');
    await startSeriesSuggestionGeneration(selectedPost.id);
    seriesPollTimeoutRef.current = setTimeout(() => {
      fetchSeriesSuggestion(selectedPost.id, { silent: true });
    }, 1200);
  };

  const getSeriesCreatableItems = () => (
    (seriesSuggestion?.suggested_articles || []).filter(item => !item.is_current_article)
  );

  const getGeneratedSeriesPostForItem = (item) => findGeneratedSeriesPost(posts, selectedPost, item);

  const getPendingSeriesItems = () => (
    getSeriesCreatableItems().filter(item => !getGeneratedSeriesPostForItem(item))
  );

  const getSeriesCreationStatusMeta = () => {
    if (selectedPost?.series_group_status === 'published') {
      return {
        label: '발행 완료',
        color: '#0f766e',
        backgroundColor: '#dcfce7'
      };
    }
    const totalCreatable = getSeriesCreatableItems().length;
    const pendingCount = getPendingSeriesItems().length;
    const generatedCount = Math.max(totalCreatable - pendingCount, 0);
    if (totalCreatable === 0) {
      return {
        label: '미작성',
        color: '#92400e',
        backgroundColor: '#fef3c7'
      };
    }
    if (generatedCount === 0) {
      return {
        label: '미작성',
        color: '#92400e',
        backgroundColor: '#fef3c7'
      };
    }
    if (pendingCount === 0) {
      return {
        label: '작성 완료',
        color: '#0f766e',
        backgroundColor: '#dcfce7'
      };
    }
    return {
      label: `일부 생성 ${generatedCount}/${totalCreatable}`,
      color: '#1d4ed8',
      backgroundColor: '#dbeafe'
    };
  };

  const getSeriesButtonMeta = () => {
    const isSeriesCompleted = selectedPost?.series_group_status === 'published';
    if (isSeriesCompleted) {
      return { label: '시리즈 완료', backgroundColor: '#0f766e' };
    }
    if (hasPersistedSeriesSuggestion(selectedPost) || hasMeaningfulSeriesSuggestion(seriesSuggestion)) {
      return { label: '시리즈 제안 완료', backgroundColor: '#0f766e' };
    }
    switch (seriesStatus?.status) {
      case 'generating':
        return { label: '제안 생성 중', backgroundColor: '#94a3b8' };
      case 'failed':
        return { label: '제안 생성 실패', backgroundColor: '#dc2626' };
      case 'unavailable':
        return { label: '시리즈 연결 필요', backgroundColor: '#9a3412' };
      case 'ready':
        return { label: '시리즈 제안 완료', backgroundColor: '#0f766e' };
      default:
        return { label: '시리즈 제안', backgroundColor: '#34495e' };
    }
  };

  const handleGenerateSeriesItems = async (items, mode = 'batch') => {
    if (!selectedPost || !items.length) return;
    if (mode === 'batch') {
      setSeriesGeneratingAll(true);
    } else {
      setSeriesGeneratingKeys(prev => [...prev, mode]);
    }
    try {
      const sourceSuggestions = seriesSuggestion?.suggested_articles || [];
      const groupTitle = seriesSuggestion?.series_group_title || stripGenerationTitle(selectedPost?.title || '');
      const currentItem = sourceSuggestions.find(item => item.is_current_article);
      const pendingItems = items.filter(item => !getGeneratedSeriesPostForItem(item));
      if (!pendingItems.length) {
        return;
      }
      const payload = {
        group_title: groupTitle,
        current_sort_order: currentItem ? sourceSuggestions.findIndex(item => item === currentItem) : null,
        items: pendingItems.map(item => ({
          title: item.title,
          topic: item.topic || item.title,
          position: item.position,
          sort_order: sourceSuggestions.findIndex(candidate =>
            candidate.position === item.position &&
            candidate.title === item.title &&
            candidate.role === item.role
          ),
          role: item.role,
          search_intent: item.search_intent,
          reader_promise: item.reader_promise,
          suggestion_item_key: makeSeriesSuggestionItemKey(item)
        }))
      };
      const res = await axios.post(`${API_URL}/posts/${selectedPost.id}/series-generate`, payload);
      await fetchPosts();
      alert(`시리즈 글 ${res.data.created_posts?.length || pendingItems.length}건 생성 요청을 등록했습니다.`);
    } catch (err) {
      alert(err.response?.data?.detail || '시리즈 글 생성 요청 실패');
    } finally {
      if (mode === 'batch') {
        setSeriesGeneratingAll(false);
      } else {
        setSeriesGeneratingKeys(prev => prev.filter(key => key !== mode));
      }
    }
  };

  const handleCleanupPublished = async () => {
    if (!window.confirm('발행 완료된 모든 글을 삭제하시겠습니까? 이미지 파일도 함께 삭제됩니다.')) return;
    try {
      const res = await axios.delete(`${API_URL}/posts/cleanup/published`);
      alert(res.data.message);
      fetchPosts();
    } catch (err) { alert('정리 실패'); }
  };

  const handleCopyAll = async () => {
    if (!editorRef.current) return;
    try {
      const savedData = await editorRef.current.save();
      const textContent = savedData.blocks.map(block => {
        if (block.type === 'paragraph' || block.type === 'header') return stripHtml(block.data.text);
        if (block.type === 'list') return block.data.items.map(item => `- ${stripHtml(item)}`).join('\n');
        if (block.type === 'table') return tableRowsToText(block.data.content);
        return '';
      }).filter(t => t !== '').join('\n\n');
      const fullText = `제목: ${title}\n\n${textContent}`;

      const htmlBlocks = savedData.blocks.map(block => {
        if (block.type === 'header') {
          const level = Math.min(Math.max(block.data.level || 2, 1), 6);
          const fontSize = level === 1 ? '30px' : level === 2 ? '24px' : '20px';
          return `<h${level} style="margin:28px 0 14px;line-height:1.45;font-size:${fontSize};font-weight:700;">${block.data.text}</h${level}>`;
        }
        if (block.type === 'paragraph') {
          return `<p style="margin:0;line-height:1.8;">${block.data.text}</p><p><br></p>`;
        }
        if (block.type === 'list') {
          const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
          return `<${tag} style="margin:0 0 18px;padding-left:24px;line-height:1.8;">${block.data.items.map(item => `<li style="margin:0 0 8px;">${item}</li>`).join('')}</${tag}>`;
        }
        if (block.type === 'table') return tableRowsToHtml(block.data.content, block.data.withHeadings);
        if (block.type === 'image') return `<div style="text-align:center;margin:24px 0;"><img src="${block.data.file.url}" style="max-width:100%;border-radius:8px;" /></div>`;
        return '';
      }).join('');
      const fullHtml = `<div style="font-family:sans-serif;font-size:16px;line-height:1.8;color:#222;"><h1 style="margin:0 0 20px;line-height:1.35;font-size:34px;font-weight:800;">${title}</h1>${htmlBlocks}</div>`;

      if (navigator.clipboard && window.ClipboardItem) {
        const data = [new ClipboardItem({ "text/html": new Blob([fullHtml], { type: "text/html" }), "text/plain": new Blob([fullText], { type: "text/plain" }) })];
        await navigator.clipboard.write(data);
      } else {
        const container = document.createElement('div');
        container.innerHTML = fullHtml;
        container.style.position = 'fixed'; container.style.left = '-9999px';
        document.body.appendChild(container);
        const range = document.createRange(); range.selectNodeContents(container);
        const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
        document.execCommand('copy');
        document.body.removeChild(container);
      }
      alert('스타일 포함 복사 완료!');
    } catch (err) { alert('복사 실패'); }
  };

  const handleDownloadImage = async () => {
    if (!viewer.imageUrl) return;
    try {
      const response = await fetch(viewer.imageUrl);
      const blob = await response.blob();
      const filename = viewer.imageUrl.split('/').pop() || 'downloaded_image.jpg';
      const isMobileDevice = isProbablyMobileDevice();

      if (isMobileDevice) {
        const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: filename,
            text: '이미지를 저장하세요.',
          });
          return;
        }

        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('이미지 다운로드 실패:', err);
      alert(isProbablyMobileDevice() ? '이미지를 열지 못했습니다.' : '이미지 저장에 실패했습니다.');
    }
  };

  const closeViewer = () => {
    setViewer(createViewerState());
    if (viewerReferenceInputRef.current) {
      viewerReferenceInputRef.current.value = '';
    }
  };

  const handleViewerReferenceImagesSelected = async (event) => {
    const files = Array.from(event.target?.files || []).filter(file => file?.type?.startsWith('image/'));
    if (!files.length) {
      return;
    }

    try {
      const loaded = await Promise.all(files.map(async (file, index) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${index}`,
        filename: normalizeReferenceImageFilename(file.name, index),
        imageDataUrl: await readFileAsDataUrl(file),
      })));
      setViewer(prev => ({
        ...prev,
        referenceImages: [...prev.referenceImages, ...loaded],
        selectedReferenceImageId: loaded[loaded.length - 1]?.id || prev.selectedReferenceImageId,
        error: '',
      }));
    } catch (err) {
      setViewer(prev => ({
        ...prev,
        error: err?.message || '참조 이미지를 읽지 못했습니다.',
      }));
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const removeViewerReferenceImage = (referenceId) => {
    setViewer(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter(item => item.id !== referenceId),
      selectedReferenceImageId: prev.selectedReferenceImageId === referenceId ? '' : prev.selectedReferenceImageId,
    }));
  };

  const applySelectedPostDetail = (detail) => {
    if (!detail?.id) return;
    setSelectedPost(prev => (prev && prev.id === detail.id ? detail : prev));
    setTitle(detail.title || '');
  };

  const extractViewerEditStatusPayload = (data) => {
    const payload = data?.current || data?.job || data?.item || data || {};
    const imageUrl = normalizeImageGuideUrl(
      payload?.replaced_image_url ||
      payload?.edited_image_url ||
      payload?.uploaded_url ||
      payload?.image_url ||
      payload?.imageUrl ||
      payload?.url
    );
    const targetImageUrl = normalizeImageGuideUrl(
      payload?.target_image_url ||
      payload?.targetImageUrl ||
      ''
    );
    return {
      requestId: `${payload?.request_id || data?.request_id || ''}`,
      jobId: `${payload?.job_id || payload?.id || payload?.photo_job_id || data?.job_id || ''}`,
      status: normalizeAsyncImageStatus(payload?.status || data?.status, imageUrl),
      imageUrl,
      targetImageUrl,
      error: payload?.error || payload?.detail || data?.detail || ''
    };
  };

  const fetchViewerEditStatus = async (postId, jobId, requestId) => {
    return axios.get(`${API_URL}/posts/${postId}/images/edit-status`, {
      params: requestId ? { request_id: requestId, ...(jobId ? { job_id: jobId } : {}) } : (jobId ? { job_id: jobId } : undefined),
    });
  };

  const pollViewerEditStatus = async (postId, jobId, sourceImageUrl = '', requestId = '') => {
    if (!postId || (!jobId && !requestId)) return;
    const normalizedSourceUrl = normalizeImageGuideUrl(sourceImageUrl);
    try {
      const res = await fetchViewerEditStatus(postId, jobId, requestId);
      const payload = extractViewerEditStatusPayload(res.data);

      if (payload.status === 'completed') {
        clearViewerEditPolling();
        await refreshPostAfterImageMutation(postId);
        setImageEditJobs(prev => {
          const next = { ...prev };
          if (normalizedSourceUrl) delete next[normalizedSourceUrl];
          if (payload.imageUrl) delete next[payload.imageUrl];
          return next;
        });
        setImageEditNotice('');
        return;
      }

      if (payload.status === 'failed') {
        clearViewerEditPolling();
        setImageEditJobs(prev => {
          const next = { ...prev };
          if (normalizedSourceUrl) {
            next[normalizedSourceUrl] = {
              ...(next[normalizedSourceUrl] || {}),
              status: 'failed',
              error: payload.error || '이미지 편집에 실패했습니다.'
            };
          }
          return next;
        });
        setImageEditNotice(payload.error || '이미지 편집에 실패했습니다.');
        return;
      }

      if (viewerEditPollAttemptsRef.current >= 60) {
        clearViewerEditPolling();
        setImageEditJobs(prev => {
          const next = { ...prev };
          if (normalizedSourceUrl) {
            next[normalizedSourceUrl] = {
              ...(next[normalizedSourceUrl] || {}),
              status: 'failed',
              error: '이미지 편집이 지연되고 있습니다. 잠시 후 다시 확인해 주세요.'
            };
          }
          return next;
        });
        setImageEditNotice('이미지 편집이 지연되고 있습니다. 잠시 후 다시 확인해 주세요.');
        return;
      }

      viewerEditPollAttemptsRef.current += 1;
      setImageEditJobs(prev => ({
        ...prev,
        [normalizedSourceUrl || payload.targetImageUrl || payload.imageUrl]: {
          ...(prev[normalizedSourceUrl || payload.targetImageUrl || payload.imageUrl] || {}),
          status: 'processing',
          jobId: payload.jobId || jobId,
          requestId: payload.requestId || requestId,
          targetImageUrl: payload.targetImageUrl || normalizedSourceUrl,
          imageUrl: payload.imageUrl || normalizedSourceUrl,
          error: ''
        }
      }));
      setImageEditNotice('이미지 편집을 진행 중입니다. 완료되면 본문 이미지가 자동으로 교체됩니다.');
      viewerEditPollTimeoutRef.current = setTimeout(() => {
        pollViewerEditStatus(postId, payload.jobId || jobId, normalizedSourceUrl || payload.targetImageUrl || payload.imageUrl, payload.requestId || requestId);
      }, 2000);
    } catch (err) {
      clearViewerEditPolling();
      const errorMessage = err.response?.data?.detail || '이미지 편집 상태를 불러오지 못했습니다.';
      setImageEditJobs(prev => {
        const next = { ...prev };
        if (normalizedSourceUrl) {
          next[normalizedSourceUrl] = {
            ...(next[normalizedSourceUrl] || {}),
            status: 'failed',
            error: errorMessage
          };
        }
        return next;
      });
      setImageEditNotice(errorMessage);
    }
  };

  const handleViewerEdit = async () => {
    const trimmedPrompt = viewer.editPrompt.trim();
    if (!selectedPost?.id || !viewer.imageUrl || !trimmedPrompt) {
      alert('편집 요청 메시지를 입력하세요.');
      return;
    }

    clearViewerEditPolling();
    setViewer(prev => ({
      ...prev,
      isEditing: true,
      isSubmittingRequest: true,
      editStatus: 'processing',
      error: '',
      editedImageUrl: ''
    }));

    try {
      const requestId = buildClientImageEditRequestId(selectedPost.id, viewer.imageUrl);
      const referenceImages = Array.isArray(viewer.referenceImages)
        ? viewer.referenceImages.map((item, index) => ({
            filename: normalizeReferenceImageFilename(item?.filename, index),
            image_data_url: `${item?.imageDataUrl || ''}`,
          })).filter(item => item.image_data_url)
        : [];
      const res = await axios.post(`${API_URL}/posts/${selectedPost.id}/images/edit`, {
        current_image_url: viewer.imageUrl,
        prompt: trimmedPrompt,
        request_id: requestId,
        reference_images: referenceImages,
      });
      const payload = extractViewerEditStatusPayload(res.data);
      const jobId = payload.jobId || `${res.data?.job_id || ''}`;
      if (!jobId && payload.status !== 'completed') {
        throw new Error('편집 작업 ID를 받지 못했습니다.');
      }

      const sourceImageUrl = normalizeImageGuideUrl(viewer.imageUrl);

      if (payload.status === 'completed') {
        await refreshPostAfterImageMutation(selectedPost.id);
        closeViewer();
        return;
      }

      if (payload.status === 'failed') {
        throw new Error(payload.error || '이미지 편집에 실패했습니다.');
      }

      setImageEditJobs(prev => ({
        ...prev,
        [sourceImageUrl]: {
          status: 'processing',
          jobId,
          requestId: payload.requestId || requestId,
          prompt: trimmedPrompt,
          referenceImageCount: referenceImages.length,
          targetImageUrl: payload.targetImageUrl || sourceImageUrl,
          imageUrl: sourceImageUrl,
          error: ''
        }
      }));
      setImageEditNotice('이미지 편집을 요청했습니다. 완료되면 본문 이미지가 자동으로 교체됩니다.');
      closeViewer();

      viewerEditPollAttemptsRef.current = 0;
      viewerEditPollTimeoutRef.current = setTimeout(() => {
        pollViewerEditStatus(selectedPost.id, jobId, sourceImageUrl, payload.requestId || requestId);
      }, 1200);
    } catch (err) {
      clearViewerEditPolling();
      setViewer(prev => ({
        ...prev,
        isEditing: false,
        isSubmittingRequest: false,
        editStatus: 'failed',
        error: err.response?.data?.detail || err.message || '이미지 편집 요청에 실패했습니다.'
      }));
    }
  };

  const imageGuides = getImageGuideBlocks(selectedPost?.content);
  const imageGuideItems = imageGuides.map((guide) => ({
    ...guide,
    job: imageGuideJobs[guide.key] || {
      key: guide.key,
      guideText: guide.guideText,
      blockIndex: guide.blockIndex,
      status: 'idle',
      imageUrl: '',
      error: ''
    }
  }));
  const pendingImageGuideItems = imageGuideItems.filter(item => item.job.status !== 'completed');
  const hasImageGuideInFlight = imageGuideItems.some(item => item.job.status === 'processing');
  const hasImageGuideStatusResolved = !isImageGuideStatusLoading && (imageGuideItems.length === 0 || Object.keys(imageGuideJobs).length > 0);
  const imageGuideButtonLabel = isImageGuideStatusLoading
    ? '이미지 상태 확인 중...'
    : hasImageGuideInFlight || isImageGuideStarting
      ? '이미지 생성 중...'
      : pendingImageGuideItems.length === 0 && hasImageGuideStatusResolved
        ? '이미지 완료'
        : `이미지 생성 ${pendingImageGuideItems.length}개`;

  const postsById = new Map(posts.map(post => [post.id, post]));
  const publishedViewPosts = posts.filter(post => isPostVisibleInViewMode(postsById, post, 'published'));

  const filteredPosts = posts
    .filter(p => isPostVisibleInViewMode(postsById, p, viewMode))
    .filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredPostMap = new Map(filteredPosts.map(post => [post.id, post]));
  const childrenByParent = filteredPosts.reduce((acc, post) => {
    if (post.series_parent_post_id && filteredPostMap.has(post.series_parent_post_id)) {
      if (!acc[post.series_parent_post_id]) acc[post.series_parent_post_id] = [];
      acc[post.series_parent_post_id].push(post);
    }
    return acc;
  }, {});
  Object.values(childrenByParent).forEach(children => children.sort(compareSeriesPosts));
  const groupRootIds = new Set(
    filteredPosts.map(post => post.series_root_post_id || post.id)
  );
  const rootPosts = Array.from(groupRootIds)
    .map(rootId => filteredPostMap.get(rootId))
    .filter(Boolean)
    .sort((a, b) => b.id - a.id);

  // Pagination Logic
  const totalPages = Math.ceil(rootPosts.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedRoots = rootPosts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const toggleSeriesGroup = (postId) => {
    setExpandedSeriesGroups(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const getSeriesGroupItems = (rootPost) => {
    const directChildren = childrenByParent[rootPost.id] || [];
    return [
      {
        ...rootPost,
        series_position: rootPost.series_position || 'current',
        series_role: rootPost.series_role || 'current',
      },
      ...directChildren,
    ].sort(compareSeriesPosts);
  };

  const renderPostRow = (post, depth = 0, includeChildren = true) => {
    const children = childrenByParent[post.id] || [];
    const hasChildren = children.length > 0;
    const canExpand = includeChildren && hasChildren;
    const isExpanded = expandedSeriesGroups[post.id] === true;
    return (
      <React.Fragment key={post.id}>
        <div
          onClick={() => selectPost(post)}
          style={{
            padding: '12px',
            paddingLeft: `${12 + depth * 18}px`,
            borderBottom: '1px solid #f1f1f1',
            cursor: 'pointer',
            borderRadius: '8px',
            marginBottom: '6px',
            backgroundColor: selectedPost?.id === post.id ? '#edf2ff' : 'white',
            position: 'relative',
            transition: 'all 0.2s'
          }}
        >
          <div style={{ position: 'absolute', right: '8px', top: '12px', display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => handleQuickStatusChange(e, post)}
              style={{ backgroundColor: post.status === 'published' ? '#95a5a6' : '#03c75a', color: 'white', border: 'none', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', cursor: 'pointer' }}
            >
              {post.status === 'published' ? '복구' : '완료'}
            </button>
            <button onClick={(e) => handleDelete(e, post.id)} style={{ color: '#e74c3c', background: 'none', border: '1px solid #e74c3c', fontSize: '9px', padding: '2px 5px', borderRadius: '4px', cursor: 'pointer' }}>삭제</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingRight: '75px' }}>
            {canExpand && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSeriesGroup(post.id);
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#475569',
                  fontSize: '12px',
                  padding: 0,
                  width: '14px'
                }}
              >
                {isExpanded ? '▾' : '▸'}
              </button>
            )}
            <strong style={{ display: 'block', marginBottom: '4px', fontSize: '13px', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stripGenerationTitle(post.title)}
            </strong>
          </div>
          <div style={{ fontSize: '10px', color: '#95a5a6', display: 'flex', gap: '6px', alignItems: 'center', marginLeft: canExpand ? '20px' : 0, flexWrap: 'wrap' }}>
            <span>ID: {post.id}</span>
            {getGenerationModeBadge(post) && (
              <span style={{
                color: getGenerationModeBadge(post).color,
                backgroundColor: getGenerationModeBadge(post).background,
                border: `1px solid ${getGenerationModeBadge(post).border}`,
                borderRadius: '10px',
                padding: '1px 6px',
                fontWeight: 'bold'
              }}>
                {getGenerationModeBadge(post).text}
              </span>
            )}
            {post.series_position && (
              <span style={{ color: '#0f766e', fontWeight: 'bold' }}>{post.series_position}</span>
            )}
            {post.series_role && (
              <span style={{ color: '#7c3aed', fontWeight: 'bold' }}>{post.series_role}</span>
            )}
            <span>| {post.status}</span>
            {getValidationBadge(post) && (
              <span style={{
                color: getValidationBadge(post).color,
                fontWeight: 'bold',
                borderLeft: '1px solid #ddd',
                paddingLeft: '6px'
              }}>
                {getValidationBadge(post).text}
              </span>
            )}
          </div>
        </div>
        {canExpand && isExpanded && (
          <div style={{ marginLeft: `${18 + depth * 18}px`, borderLeft: '2px solid #e5e7eb', paddingLeft: '8px' }}>
            {children.sort(compareSeriesPosts).map(child => renderPostRow(child, depth + 1, true))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderPostGroup = (rootPost) => {
    const directChildren = childrenByParent[rootPost.id] || [];
    const hasSeriesGroup = directChildren.length > 0;
    if (!hasSeriesGroup) {
      return renderPostRow(rootPost, 0);
    }

    const isExpanded = expandedSeriesGroups[rootPost.id] === true;
    const groupItems = getSeriesGroupItems(rootPost);
    return (
      <div
        key={`group-${rootPost.id}`}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          backgroundColor: '#f8fafc',
          marginBottom: '10px',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            padding: '12px',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <button
            type="button"
            onClick={() => toggleSeriesGroup(rootPost.id)}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#1e293b',
              fontWeight: 'bold',
              fontSize: '13px',
              textAlign: 'left',
              flex: 1,
              padding: 0
            }}
          >
            <span style={{ width: '14px', color: '#475569' }}>{isExpanded ? '▾' : '▸'}</span>
            <span>{getSeriesGroupTitle(rootPost)}</span>
            <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 'normal' }}>
              {groupItems.length}편
            </span>
          </button>
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button
              onClick={(e) => handleSeriesStatusChange(e, rootPost)}
              style={{ backgroundColor: getSeriesGroupCompletionStatus(rootPost) === 'published' ? '#95a5a6' : '#03c75a', color: 'white', border: 'none', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', cursor: 'pointer' }}
            >
              {getSeriesGroupCompletionStatus(rootPost) === 'published' ? '그룹 복구' : '그룹 완료'}
            </button>
            <button
              onClick={(e) => handleSeriesDelete(e, rootPost.id)}
              style={{ color: '#e74c3c', background: 'none', border: '1px solid #e74c3c', fontSize: '9px', padding: '2px 5px', borderRadius: '4px', cursor: 'pointer' }}
            >
              그룹 삭제
            </button>
          </div>
        </div>
        {isExpanded && (
          <div style={{ padding: '8px' }}>
            {groupItems.map(item => renderPostRow(item, 0, item.id !== rootPost.id))}
          </div>
        )}
      </div>
    );
  };

  const correctionSuggestions = getCorrectionSuggestions(selectedPost?.correction_report);
  const appliedSuggestions = getAppliedSuggestions(selectedPost?.correction_report);
  const remainingRisks = getRemainingRisks(selectedPost?.correction_report);
  const shouldShowActivity =
    selectedPost &&
    (activityEvents.length > 0 ||
      selectedPost.status === 'processing' ||
      selectedPost.validation_status === 'validating' ||
      selectedPost.correction_status === 'correcting' ||
      activityError);

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100vh', fontFamily: 'sans-serif', backgroundColor: '#f4f7f6' }}>
      {(!isMobile || !selectedPost) && (
        <div style={{ width: isMobile ? '100%' : '350px', borderRight: '1px solid #ddd', padding: '15px', overflowY: 'auto', backgroundColor: 'white', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '15px', marginTop: '10px' }}>NBAP Dashboard</h2>
          
          <div style={{ marginBottom: '20px', padding: '15px', borderRadius: '12px', background: 'linear-gradient(135deg, #6e8efb, #a777e3)', color: 'white', flexShrink: 0 }}>
            <h4 style={{ marginTop: 0, marginBottom: '10px' }}>AI 새 글 생성</h4>
            <input style={{ width: '100%', padding: '10px', marginBottom: '10px', border: 'none', borderRadius: '6px', boxSizing: 'border-box' }} placeholder="주제를 입력하세요" value={topic} onChange={(e) => setTopic(e.target.value)} disabled={isGenerating} />
            <button onClick={handleGenerate} disabled={isGenerating} style={{ width: '100%', padding: '10px', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid white', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>{isGenerating ? '생성 중...' : '글 생성'}</button>
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9, marginBottom: '8px' }}>이미지 생성</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => setIsImageGenerationEnabled(true)}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: isImageGenerationEnabled ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: isImageGenerationEnabled ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: isImageGenerationEnabled ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  포함
                </button>
                <button
                  type="button"
                  onClick={() => setIsImageGenerationEnabled(false)}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: !isImageGenerationEnabled ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: !isImageGenerationEnabled ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: !isImageGenerationEnabled ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  제외
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>생성 버전</div>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setIsVersionInfoOpen(prev => !prev)}
                    onMouseEnter={() => setIsVersionInfoOpen(true)}
                    onMouseLeave={() => !isMobile && setIsVersionInfoOpen(false)}
                    disabled={isGenerating}
                    aria-label="생성 버전 안내"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.65)',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isGenerating ? 'default' : 'pointer',
                      padding: 0
                    }}
                  >
                    i
                  </button>
                  {isVersionInfoOpen && (
                    <div
                      onMouseEnter={() => setIsVersionInfoOpen(true)}
                      onMouseLeave={() => !isMobile && setIsVersionInfoOpen(false)}
                      style={{
                        position: 'absolute',
                        top: '24px',
                        left: 0,
                        width: '220px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(15, 23, 42, 0.94)',
                        color: 'white',
                        fontSize: '11px',
                        lineHeight: 1.55,
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.28)',
                        zIndex: 10,
                        textAlign: 'left'
                      }}
                    >
                      안정 버전: 지속 운영되던 버전이며 동시에 최대 3개까지 생성됩니다.
                      <br />
                      <br />
                      베타 버전: 현재 개발 중인 버전이며 1개씩 순차 실행됩니다.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => setGenerationVersion('stable')}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: generationVersion === 'stable' ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: generationVersion === 'stable' ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: generationVersion === 'stable' ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  안정 버전
                </button>
                <button
                  type="button"
                  onClick={() => setGenerationVersion('beta')}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: generationVersion === 'beta' ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: generationVersion === 'beta' ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: generationVersion === 'beta' ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  베타 버전
                </button>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9, marginBottom: '8px' }}>모델 선택</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setGenerationModel('gpt')}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: generationModel === 'gpt' ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: generationModel === 'gpt' ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: generationModel === 'gpt' ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  GPT
                </button>
                <button
                  type="button"
                  onClick={() => setGenerationModel('gemini')}
                  disabled={isGenerating}
                  style={{
                    flex: 1,
                    padding: '9px 10px',
                    borderRadius: '999px',
                    border: generationModel === 'gemini' ? '1px solid white' : '1px solid rgba(255,255,255,0.35)',
                    backgroundColor: generationModel === 'gemini' ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: 'white',
                    fontWeight: generationModel === 'gemini' ? 'bold' : 'normal',
                    cursor: isGenerating ? 'default' : 'pointer'
                  }}
                >
                  Gemini
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', marginBottom: '10px', borderBottom: '2px solid #eee', flexShrink: 0 }}>
            <button 
              onClick={() => { setViewMode('active'); setCurrentPage(1); }}
              style={{ flex: 1, padding: '10px', border: 'none', background: 'none', borderBottom: viewMode === 'active' ? '3px solid #6e8efb' : 'none', fontWeight: viewMode === 'active' ? 'bold' : 'normal', cursor: 'pointer' }}
            >
              미발행 ({posts.filter(p => p.status !== 'published').length})
            </button>
            <button 
              onClick={() => { setViewMode('published'); setCurrentPage(1); }}
              style={{ flex: 1, padding: '10px', border: 'none', background: 'none', borderBottom: viewMode === 'published' ? '3px solid #6e8efb' : 'none', fontWeight: viewMode === 'published' ? 'bold' : 'normal', cursor: 'pointer' }}
            >
              발행완료 ({publishedViewPosts.length})
            </button>
          </div>

          <div style={{ marginBottom: '15px', flexShrink: 0 }}>
            <input 
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '20px', boxSizing: 'border-box', outline: 'none', fontSize: '14px' }}
              placeholder="🔍 목록 검색..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexShrink: 0 }}>
             <h3 style={{ margin: 0, fontSize: '15px' }}>목록 {searchTerm && `(${filteredPosts.length})`}</h3>
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ padding: '2px 5px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value={10}>10개씩</option>
                  <option value={20}>20개씩</option>
                  <option value={50}>50개씩</option>
                </select>
                {viewMode === 'published' && filteredPosts.length > 0 && (
                  <button onClick={handleCleanupPublished} style={{ fontSize: '11px', color: '#e74c3c', border: '1px solid #e74c3c', background: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer' }}>정리</button>
                )}
             </div>
          </div>

          <div style={{ flex: 1 }}>
            {displayedRoots.map(post => renderPostGroup(post))}
            
            {/* Pagination Controls */}
            {filteredPosts.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '15px', marginBottom: '10px' }}>
                <button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 1}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  &lt;
                </button>
                <span style={{ fontSize: '13px', color: '#555' }}>
                  {currentPage} / {totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                  style={{ padding: '5px 10px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === totalPages ? '#f5f5f5' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                >
                  &gt;
                </button>
              </div>
            )}

            {filteredPosts.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', marginTop: '20px', fontSize: '14px' }}>글이 없습니다.</div>
            )}
          </div>
        </div>
      )}
      {(selectedPost || !isMobile) && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white', position: 'relative' }}>
          {selectedPost ? (
            <>
              <div style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'white', borderBottom: '1px solid #eee', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {isMobile && <button onClick={() => selectPost(null)} style={{ marginRight: '10px' }}>← 목록</button>} 
                  <span style={{ fontSize: '14px', color: isDirty ? '#e67e22' : '#27ae60' }}>{isDirty ? '● 저장 필요' : '✓ 저장됨'}</span>
                  {getGenerationModeBadge(selectedPost) && (
                    <div style={{
                      fontSize: '13px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: getGenerationModeBadge(selectedPost).background,
                      color: getGenerationModeBadge(selectedPost).color,
                      fontWeight: 'bold',
                      border: `1px solid ${getGenerationModeBadge(selectedPost).border}`
                    }}>
                      {getGenerationModeBadge(selectedPost).text}
                    </div>
                  )}
                  {getValidationBadge(selectedPost) && (
                    <div style={{ 
	                      fontSize: '13px', 
	                      padding: '4px 10px', 
	                      borderRadius: '20px', 
	                      backgroundColor: getValidationBadge(selectedPost).background,
	                      color: getValidationBadge(selectedPost).color,
	                      fontWeight: 'bold',
	                      border: `1px solid ${getValidationBadge(selectedPost).border}`
	                    }}>
	                      {getValidationBadge(selectedPost).text}
	                    </div>
	                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handleCopyAll} style={{ padding: '8px 16px', backgroundColor: '#4a90e2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>전체 복사</button>
                  <button onClick={handleSave} style={{ padding: '8px 16px', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>저장</button>
                  <button
                    onClick={() => handleGenerateImageGuides(pendingImageGuideItems)}
                    disabled={isImageGuideStatusLoading || pendingImageGuideItems.length === 0 || hasImageGuideInFlight || isImageGuideStarting}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: isImageGuideStatusLoading || pendingImageGuideItems.length === 0 ? '#cbd5e1' : '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isImageGuideStatusLoading || pendingImageGuideItems.length === 0 || hasImageGuideInFlight || isImageGuideStarting ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {imageGuideButtonLabel}
                  </button>
                  <button
                    onClick={handleOpenSeriesModal}
                    style={{ padding: '8px 16px', backgroundColor: getSeriesButtonMeta().backgroundColor, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {getSeriesButtonMeta().label}
                  </button>
                  <button 
                    onClick={handleCorrectPost}
                    disabled={isCorrectionButtonDisabled(selectedPost, correctingPostId)}
                    style={{ 
                      padding: '8px 16px', 
                      backgroundColor: isCorrectionButtonDisabled(selectedPost, correctingPostId) ? '#bdc3c7' : '#8e44ad', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '6px', 
                      cursor: isCorrectionButtonDisabled(selectedPost, correctingPostId) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {getCorrectionButtonLabel(selectedPost, correctingPostId)}
                  </button>
                  <button 
                    onClick={handleMarkAsPublished} 
                    style={{ padding: '8px 16px', backgroundColor: selectedPost.status === 'published' ? '#95a5a6' : '#03c75a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {selectedPost.status === 'published' ? '미발행 전환' : '발행 완료 표시'}
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, padding: isMobile ? '20px' : '40px', overflowY: 'auto' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                  {selectedPost.generation_mode === 'test' && (
                    <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', color: '#115e59', fontSize: '13px', lineHeight: 1.6 }}>
                      테스트 생성 결과입니다. 운영 글과 같은 화면에서 본문 확인, 교정, 발행까지 가능하지만 목록과 상태에서는 테스트 글로 계속 구분됩니다.
                    </div>
                  )}
                  <input style={{ width: '100%', fontSize: isMobile ? '24px' : '32px', fontWeight: 'bold', border: 'none', borderBottom: '2px solid #f1f1f1', marginBottom: '20px', outline: 'none' }} value={title} onChange={(e) => { setTitle(e.target.value); setIsDirty(true); }} placeholder="제목을 입력하세요" />
                  {selectedPost.correction_report && (
                    <section style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', backgroundColor: '#fbfbfd' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>교정 변경 내역</h3>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#5f6c7b', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>제안 {correctionSuggestions.length}개 · 적용 {appliedSuggestions.length}개 · 남은 리스크 {remainingRisks.length}개</span>
                          <button
                            type="button"
                            onClick={() => setIsCorrectionHistoryOpen(!isCorrectionHistoryOpen)}
                            style={{ border: '1px solid #d0d7de', backgroundColor: 'white', color: '#34495e', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            {isCorrectionHistoryOpen ? '닫기' : '보기'}
                          </button>
                        </div>
                      </div>
                      {isCorrectionHistoryOpen && correctionSuggestions.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {correctionSuggestions.map((item, index) => (
                            <div key={item.suggestion_id || index} style={{ borderTop: index === 0 ? 'none' : '1px solid #eceff1', paddingTop: index === 0 ? 0 : '10px' }}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#8e44ad', border: '1px solid #d7bde2', borderRadius: '12px', padding: '2px 8px' }}>
                                  {item.action || 'edit'}
                                </span>
                                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>{item.severity || 'normal'}</span>
                                {item.reason && <span style={{ fontSize: '12px', color: '#34495e' }}>{item.reason}</span>}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                                <div style={{ backgroundColor: '#fff5f5', border: '1px solid #fadbd8', borderRadius: '6px', padding: '10px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#c0392b', marginBottom: '6px' }}>Before</div>
                                  <div style={{ fontSize: '13px', lineHeight: 1.5, color: '#2c3e50', whiteSpace: 'pre-wrap' }}>{item.before || '-'}</div>
                                </div>
                                <div style={{ backgroundColor: '#f1fbf6', border: '1px solid #d5f5e3', borderRadius: '6px', padding: '10px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#229954', marginBottom: '6px' }}>After</div>
                                  <div style={{ fontSize: '13px', lineHeight: 1.5, color: '#2c3e50', whiteSpace: 'pre-wrap' }}>{item.after || '-'}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : isCorrectionHistoryOpen ? (
                        <div style={{ fontSize: '13px', color: '#7f8c8d' }}>표시할 교정 제안이 없습니다.</div>
                      ) : null}
                    </section>
                  )}
                  {shouldShowActivity && (
                    <section style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '12px 16px', marginBottom: '18px', backgroundColor: '#ffffff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>진행 로그</h3>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12px', color: activityError ? '#e67e22' : '#5f6c7b' }}>
                            {activityError || `${activityEvents.length}개 이벤트`}
                          </span>
                          <button
                            type="button"
                            onClick={() => setIsActivityOpen(!isActivityOpen)}
                            style={{ border: '1px solid #d0d7de', backgroundColor: 'white', color: '#34495e', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            {isActivityOpen ? '닫기' : '보기'}
                          </button>
                        </div>
                      </div>
                      {isActivityOpen && activityEvents.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {activityEvents.map((event, index) => (
                            <div key={`${event.stage}-${event.status}-${index}`} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '64px 1fr auto', gap: '8px', alignItems: 'start', fontSize: '13px' }}>
                              <span style={{ color: getEventColor(event.status), fontWeight: 'bold' }}>{getStageLabel(event.stage)}</span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ color: '#2c3e50', lineHeight: 1.5 }}>{event.message}</span>
                                {event.model && (
                                  <span style={{ color: '#5f6c7b', fontSize: '12px' }}>
                                    모델: {event.model}
                                  </span>
                                )}
                              </div>
                              <span style={{ color: '#95a5a6', fontSize: '12px' }}>{formatEventTime(event.timestamp)}</span>
                            </div>
                          ))}
                        </div>
                      ) : isActivityOpen ? (
                        <div style={{ fontSize: '13px', color: '#7f8c8d' }}>아직 표시할 진행 로그가 없습니다.</div>
                      ) : null}
                    </section>
                  )}
                  {imageGuideItems.length > 0 && (
                    <section style={{ border: '1px solid #dbeafe', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px', backgroundColor: '#f8fbff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '16px', color: '#1e3a8a' }}>본문 이미지 가이드</h3>
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                            `[이미지 가이드: ...]` 문단을 감지했습니다. 생성이 끝나면 본문 안에서 실제 이미지로 교체됩니다.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleGenerateImageGuides(pendingImageGuideItems)}
                          disabled={isImageGuideStatusLoading || pendingImageGuideItems.length === 0 || hasImageGuideInFlight || isImageGuideStarting}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: isImageGuideStatusLoading || pendingImageGuideItems.length === 0 ? '#cbd5e1' : '#1d4ed8',
                            color: 'white',
                            cursor: isImageGuideStatusLoading || pendingImageGuideItems.length === 0 || hasImageGuideInFlight || isImageGuideStarting ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          {isImageGuideStatusLoading ? '상태 확인 중' : hasImageGuideInFlight || isImageGuideStarting ? '생성 진행 중' : pendingImageGuideItems.length === 0 ? '모두 완료' : `남은 가이드 ${pendingImageGuideItems.length}개 생성`}
                        </button>
                      </div>
                      {imageGuideError && (
                        <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', fontSize: '12px', lineHeight: 1.5 }}>
                          {imageGuideError}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {imageGuideItems.map((item) => (
                          <div key={item.key} style={{ border: '1px solid #dbe3ef', borderRadius: '10px', padding: '12px', backgroundColor: 'white' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937' }}>
                                {item.guideText}
                              </div>
                              <span style={{
                                fontSize: '11px',
                                fontWeight: 'bold',
                                color: item.job.status === 'completed' ? '#0f766e' : item.job.status === 'failed' ? '#b91c1c' : item.job.status === 'processing' ? '#1d4ed8' : '#64748b',
                                backgroundColor: item.job.status === 'completed' ? '#dcfce7' : item.job.status === 'failed' ? '#fee2e2' : item.job.status === 'processing' ? '#dbeafe' : '#e5e7eb',
                                borderRadius: '999px',
                                padding: '4px 8px'
                              }}>
                                {item.job.status === 'completed' ? '완료' : item.job.status === 'failed' ? '실패' : item.job.status === 'processing' ? '생성 중' : '대기'}
                              </span>
                            </div>
                            {item.job.status === 'completed' && item.job.imageUrl ? (
                              <img
                                src={item.job.imageUrl}
                                alt={item.guideText}
                                style={{ width: '100%', maxWidth: '360px', display: 'block', borderRadius: '10px', border: '1px solid #dbe3ef', objectFit: 'cover' }}
                              />
                            ) : item.job.status === 'processing' ? (
                              <div style={{ width: '100%', maxWidth: '360px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #dbe3ef', background: 'linear-gradient(110deg, #eff6ff 8%, #dbeafe 18%, #eff6ff 33%)', backgroundSize: '200% 100%', minHeight: '220px', animation: 'nbapImageGuideSkeleton 1.4s ease-in-out infinite' }} />
                            ) : (
                              <div style={{ fontSize: '12px', color: item.job.error ? '#b91c1c' : '#64748b', lineHeight: 1.6 }}>
                                {item.job.error || '아직 생성되지 않았습니다.'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                  {imageEditNotice && (
                    <div style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#334155', fontSize: '12px', lineHeight: 1.5 }}>
                      {imageEditNotice}
                    </div>
                  )}
                  {/* Editor JS area */}
                  <div id="editorjs" style={{ minHeight: '60vh' }}></div>
                </div>
              </div>

              {/* Image Viewer Modal */}
              {isSeriesModalOpen && (
                <div
                  onClick={() => {
                    clearSeriesPolling();
                    setIsSeriesModalOpen(false);
                  }}
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15,23,42,0.58)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '16px' : '28px' }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '100%', maxWidth: '960px', maxHeight: '88vh', overflowY: 'auto', backgroundColor: 'white', borderRadius: '18px', boxShadow: '0 24px 80px rgba(15,23,42,0.28)', padding: isMobile ? '18px' : '24px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold', marginBottom: '6px' }}>블로그 올인원 전문가 제안</div>
                        <h3 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px', lineHeight: 1.35, color: '#1f2937' }}>이 글을 시리즈로 확장할까요?</h3>
                        {selectedPost && (
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#5f6c7b', lineHeight: 1.6 }}>
                            현재 글 기준: <strong>{selectedPost.title}</strong><br />
                            생성 모드: <strong>{selectedPost.generation_mode === 'test' ? 'Beta' : '안정'}</strong> · 모델: <strong>{selectedPost.llm_provider === 'gemini' ? 'Gemini' : 'GPT'}</strong>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          clearSeriesPolling();
                          setIsSeriesModalOpen(false);
                        }}
                        style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                      >
                        ✕
                      </button>
                    </div>

                    {seriesLoading ? (
                      <div style={{ padding: '24px 0', color: '#475569', lineHeight: 1.7 }}>시리즈 제안을 생성하는 중입니다...</div>
                    ) : (
                      <>
                        {seriesStatus?.status === 'failed' && (
                          <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '13px', color: '#7f1d1d', fontWeight: 'bold' }}>이전 시리즈 제안 생성이 실패했습니다.</div>
                            <button
                              type="button"
                              onClick={handleRetrySeriesSuggestion}
                              disabled={seriesRetrying}
                              style={{
                                padding: '9px 14px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: seriesRetrying ? '#cbd5e1' : '#b91c1c',
                                color: 'white',
                                cursor: seriesRetrying ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold'
                              }}
                            >
                              {seriesRetrying ? '재시도 중...' : '재시도'}
                            </button>
                          </div>
                        )}
                        {seriesError && (
                          <div style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '10px', backgroundColor: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', fontSize: '13px', lineHeight: 1.6 }}>
                            {seriesError}
                          </div>
                        )}
                        {Array.isArray(seriesStatus?.failure_history) && seriesStatus.failure_history.length > 0 && (
                          <div style={{ marginBottom: '18px', border: '1px solid #fecdd3', borderRadius: '12px', padding: '14px 16px', backgroundColor: '#fff7f7' }}>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#7f1d1d', marginBottom: '8px' }}>실패 이력</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {seriesStatus.failure_history.map((item, index) => (
                                <div key={`${item.failed_at || 'failed'}-${index}`} style={{ fontSize: '12px', color: '#7f1d1d', lineHeight: 1.6 }}>
                                  <strong>{item.failed_at || '시간 미상'}</strong><br />
                                  {item.error}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {seriesSuggestion && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                                  추천 시리즈 구성
                                </div>
                                {seriesSuggestion.series_group_title && (
                                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    그룹명: {seriesSuggestion.series_group_title}
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f766e', backgroundColor: '#dcfce7', borderRadius: '999px', padding: '4px 9px' }}>
                                    제안 상태: {selectedPost?.series_suggestion_status === 'generated' || seriesStatus?.status === 'generated' ? '완료' : seriesStatus?.status === 'ready' ? '완료' : seriesStatus?.status === 'generating' ? '생성 중' : seriesStatus?.status === 'failed' ? '실패' : '대기'}
                                  </span>
                                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: getSeriesCreationStatusMeta().color, backgroundColor: getSeriesCreationStatusMeta().backgroundColor, borderRadius: '999px', padding: '4px 9px' }}>
                                    시리즈 작성 상태: {getSeriesCreationStatusMeta().label}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleGenerateSeriesItems(getPendingSeriesItems(), 'batch')}
                                disabled={seriesGeneratingAll || getPendingSeriesItems().length === 0}
                                style={{
                                  padding: '9px 14px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: seriesGeneratingAll || getPendingSeriesItems().length === 0 ? '#cbd5e1' : '#2563eb',
                                  color: 'white',
                                  cursor: seriesGeneratingAll || getPendingSeriesItems().length === 0 ? 'not-allowed' : 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                {seriesGeneratingAll ? '시리즈 생성 중...' : getPendingSeriesItems().length === 0 ? '시리즈 작성 완료' : '시리즈 한번에 생성'}
                              </button>
                            </div>

                            <div style={{ marginBottom: '18px' }}>
                              <button
                                type="button"
                                onClick={() => setIsSeriesAnalysisOpen(prev => !prev)}
                                style={{
                                  border: '1px solid #dbe3ef',
                                  backgroundColor: '#f8fafc',
                                  color: '#334155',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {isSeriesAnalysisOpen ? '시리즈 판단 접기' : '시리즈 판단 보기'}
                              </button>
                            </div>

                            {isSeriesAnalysisOpen && (
                              <>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: '14px', marginBottom: '18px' }}>
                                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px 16px', backgroundColor: '#fafafa' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px' }}>현재 글 역할</div>
                                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937', marginBottom: '6px' }}>
                                      {seriesSuggestion.current_article_role?.label || 'other'}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.65 }}>
                                      {seriesSuggestion.current_article_role?.reason || '-'}
                                    </div>
                                  </div>
                                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px 16px', backgroundColor: '#fafafa' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px' }}>시리즈 판단</div>
                                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1f2937', marginBottom: '6px' }}>
                                      {seriesSuggestion.series_verdict || 'standalone_only'}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.65 }}>
                                      {seriesSuggestion.strategy_summary || '-'}
                                    </div>
                                  </div>
                                </div>

                                {Array.isArray(seriesSuggestion.why_series_or_not) && seriesSuggestion.why_series_or_not.length > 0 && (
                                  <div style={{ marginBottom: '18px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>판단 근거</div>
                                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569', lineHeight: 1.75, fontSize: '13px' }}>
                                      {seriesSuggestion.why_series_or_not.map((item, index) => (
                                        <li key={`${item}-${index}`}>{item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {(seriesSuggestion.suggested_articles || []).map((item, index) => {
                                const key = `${item.position}-${item.title}`;
                                const isGeneratingItem = seriesGeneratingKeys.includes(key);
                                const generatedPost = getGeneratedSeriesPostForItem(item);
                                const isItemDisabled = item.is_current_article || seriesGeneratingAll || isGeneratingItem || !!generatedPost;
                                return (
                                  <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: '14px', padding: '14px 16px', backgroundColor: item.is_current_article ? '#f8fafc' : 'white' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#2563eb', backgroundColor: '#dbeafe', borderRadius: '999px', padding: '3px 8px' }}>
                                            {item.position}
                                          </span>
                                          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f766e', backgroundColor: '#ccfbf1', borderRadius: '999px', padding: '3px 8px' }}>
                                            {item.role}
                                          </span>
                                          {item.is_current_article && (
                                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#7c3aed', backgroundColor: '#ede9fe', borderRadius: '999px', padding: '3px 8px' }}>
                                              현재 글
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ fontSize: '17px', fontWeight: 'bold', color: '#111827', lineHeight: 1.45, marginBottom: '8px' }}>
                                          {item.title}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.7, marginBottom: '8px' }}>
                                          <strong>주제 입력:</strong> {item.topic}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.7, marginBottom: '6px' }}>
                                          <strong>검색 의도:</strong> {item.search_intent}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.7, marginBottom: '6px' }}>
                                          <strong>독자 약속:</strong> {item.reader_promise}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.7, marginBottom: '6px' }}>
                                          <strong>제안 이유:</strong> {item.why_needed}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.7 }}>
                                          <strong>마무리 방향:</strong> {item.cta_mode}
                                        </div>
                                        {generatedPost && (
                                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#0f766e', fontWeight: 'bold' }}>
                                            생성된 글 ID: {generatedPost.id}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <button
                                          type="button"
                                          onClick={() => handleGenerateSeriesItems([item], key)}
                                          disabled={isItemDisabled}
                                          style={{
                                            padding: '9px 12px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            backgroundColor: isItemDisabled ? '#cbd5e1' : '#0f766e',
                                            color: 'white',
                                            cursor: isItemDisabled ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            minWidth: '120px'
                                          }}
                                        >
                                          {item.is_current_article ? '현재 글' : generatedPost ? `생성 완료 ID ${generatedPost.id}` : isGeneratingItem ? '생성 중...' : '이 글만 생성'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {viewer.isOpen && (
                <div 
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                  onClick={closeViewer}
                >
                  <div
                    style={{
                      width: 'min(960px, 100%)',
                      height: 'min(92vh, 920px)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      minHeight: 0
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={() => setViewer(prev => ({ ...prev, isHovered: true }))}
                      onMouseLeave={() => setViewer(prev => ({ ...prev, isHovered: false }))}
                    >
                      <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '12px', zIndex: 2 }}>
                        <button
                          onClick={handleDownloadImage}
                          disabled={viewer.isEditing}
                          style={{ padding: '10px 20px', backgroundColor: viewer.isEditing ? '#95a5a6' : '#2ecc71', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: viewer.isEditing ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}
                        >
                          {isProbablyMobileDevice() ? '앨범에 저장' : '원본 저장 📥'}
                        </button>
                      </div>
                      <img 
                        src={viewer.imageUrl} 
                        alt="Expanded view" 
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', cursor: isProbablyMobileDevice() ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (isProbablyMobileDevice() && !viewer.isEditing) {
                            handleDownloadImage();
                          }
                        }} 
                      />
                      {viewer.isSubmittingRequest && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(15, 23, 42, 0.52)',
                            borderRadius: '12px',
                            zIndex: 1
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '12px',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            <div
                              style={{
                                width: '34px',
                                height: '34px',
                                borderRadius: '50%',
                                border: '3px solid rgba(255,255,255,0.24)',
                                borderTopColor: '#ffffff',
                                animation: 'nbapSpin 0.9s linear infinite'
                              }}
                            />
                            <div>편집 요청 전송 중...</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        width: '100%',
                        backgroundColor: 'rgba(15, 23, 42, 0.9)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}
                    >
                      <input
                        ref={viewerReferenceInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleViewerReferenceImagesSelected}
                        style={{ display: 'none' }}
                      />
                      <div
                        style={{
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}
                      >
                        <div style={{ color: '#cbd5e1', fontSize: '13px' }}>
                          참조 이미지를 추가하면 톤, 배경, 구도를 참고해서 편집할 수 있습니다.
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            minHeight: '96px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => viewerReferenceInputRef.current?.click()}
                            disabled={viewer.isEditing}
                            style={{
                              width: '52px',
                              height: '52px',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: viewer.isEditing ? '#64748b' : '#0f766e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '999px',
                              fontSize: '28px',
                              lineHeight: 1,
                              fontWeight: 'bold',
                              cursor: viewer.isEditing ? 'not-allowed' : 'pointer'
                            }}
                            aria-label="참조 이미지 추가"
                          >
                            +
                          </button>
                          <div
                            style={{
                              display: 'flex',
                              gap: '10px',
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              paddingBottom: '4px',
                              flex: 1,
                              minWidth: 0,
                              scrollbarWidth: 'thin',
                              WebkitOverflowScrolling: 'touch'
                            }}
                          >
                            {viewer.referenceImages.length === 0 ? (
                              <div
                                style={{
                                  minWidth: '160px',
                                  height: '84px',
                                  borderRadius: '12px',
                                  border: '1px dashed rgba(255,255,255,0.18)',
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                  color: '#94a3b8',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '0 12px',
                                  textAlign: 'center'
                                }}
                              >
                                참조 이미지를 추가하면 여기에 썸네일이 표시됩니다.
                              </div>
                            ) : viewer.referenceImages.map((item, index) => (
                              <div
                                key={item.id || `${item.filename}-${index}`}
                                onClick={() => setViewer(prev => ({ ...prev, selectedReferenceImageId: item.id }))}
                                style={{
                                  position: 'relative',
                                  minWidth: '92px',
                                  width: '92px',
                                  flexShrink: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px',
                                  padding: '6px',
                                  backgroundColor: 'rgba(255,255,255,0.08)',
                                  border: item.id === viewer.selectedReferenceImageId ? '1px solid #60a5fa' : '1px solid rgba(255,255,255,0.12)',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  boxShadow: item.id === viewer.selectedReferenceImageId ? '0 0 0 2px rgba(96,165,250,0.2)' : 'none'
                                }}
                              >
                                {item.id === viewer.selectedReferenceImageId && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeViewerReferenceImage(item.id);
                                    }}
                                    disabled={viewer.isEditing}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      padding: '4px 7px',
                                      backgroundColor: viewer.isEditing ? '#64748b' : 'rgba(185,28,28,0.94)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '999px',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      cursor: viewer.isEditing ? 'not-allowed' : 'pointer',
                                      zIndex: 1
                                    }}
                                  >
                                    삭제
                                  </button>
                                )}
                                <img
                                  src={item.imageDataUrl}
                                  alt={item.filename}
                                  style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                                <div
                                  style={{
                                    color: '#e2e8f0',
                                    fontSize: '10px',
                                    lineHeight: 1.3,
                                    wordBreak: 'break-all',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: '26px'
                                  }}
                                >
                                  {item.filename}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <textarea
                          ref={viewerPromptRef}
                          value={viewer.editPrompt}
                          onChange={(e) => setViewer(prev => ({ ...prev, editPrompt: e.target.value }))}
                          placeholder="예: 배경을 더 밝게 하고 제품에 집중되도록 정리해 주세요."
                          disabled={viewer.isEditing}
                          rows={4}
                          style={{
                            width: '100%',
                            resize: 'vertical',
                            minHeight: '96px',
                            padding: '12px 14px',
                            borderRadius: '10px',
                            border: '1px solid rgba(255,255,255,0.14)',
                            backgroundColor: 'rgba(255,255,255,0.96)',
                            color: '#111827',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <div style={{ color: '#e2e8f0', fontSize: '14px' }}>
                          {viewer.isSubmittingRequest
                            ? '편집 요청을 전송하고 있습니다. 응답이 오면 창이 닫히고 본문에서 진행 상태가 표시됩니다.'
                            : viewer.isEditing
                            ? '이미지 편집을 요청했고 결과를 확인하는 중입니다.'
                            : viewer.editStatus === 'completed'
                              ? '편집이 완료되어 최신 이미지를 표시 중입니다.'
                              : `편집 메시지를 입력한 뒤 이미지를 다시 생성할 수 있습니다.${viewer.referenceImages.length ? ` 참조 이미지 ${viewer.referenceImages.length}개가 함께 전달됩니다.` : ''}`}
                          {viewer.error && (
                            <div style={{ marginTop: '6px', color: '#fca5a5' }}>
                              {viewer.error}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          position: 'sticky',
                          bottom: 0,
                          padding: '14px 16px 16px',
                          background: 'linear-gradient(180deg, rgba(15,23,42,0.88), rgba(15,23,42,0.98))',
                          borderTop: '1px solid rgba(255,255,255,0.08)',
                          display: 'flex',
                          gap: '10px',
                          justifyContent: 'flex-end',
                          zIndex: 2
                        }}
                      >
                        <button
                          onClick={closeViewer}
                          disabled={viewer.isEditing}
                          style={{
                            flex: isMobile ? 1 : '0 0 auto',
                            padding: '12px 18px',
                            backgroundColor: viewer.isEditing ? '#7f8c8d' : '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: 'bold',
                            cursor: viewer.isEditing ? 'not-allowed' : 'pointer'
                          }}
                        >
                          닫기
                        </button>
                        <button
                          onClick={handleViewerEdit}
                          disabled={viewer.isEditing || !viewer.editPrompt.trim()}
                          style={{
                            flex: isMobile ? 1 : '0 0 auto',
                            padding: '12px 18px',
                            backgroundColor: viewer.isEditing || !viewer.editPrompt.trim() ? '#64748b' : '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: 'bold',
                            cursor: viewer.isEditing || !viewer.editPrompt.trim() ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {viewer.isSubmittingRequest ? '요청 전송 중...' : viewer.isEditing ? '편집 중...' : '편집'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#bdc3c7' }}><h2>글을 선택해 주세요</h2></div>}
        </div>
      )}
      <style>{`
        @keyframes nbapSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes nbapImageGuideSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

export default App;
